'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import Link from '@/components/NoPrefetchLink';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { fetchPlayerSearch } from '@/lib/apiClient';
import type {
    Team,
    PlayerSearchItem,
    MlbTeam,
    PlayerSearchFilters,
    DefenseFilterScope,
    PitcherCareerComparisonOperator,
    PitcherCareerFilterCondition,
} from '@/lib/apiClient';
import { formatBirthday, translatePosition, translateNationality } from '@/lib/format';
import { APP_NAME } from '@/lib/constants';
import { TEAM_COLORS } from '@/lib/teamColors';
import type { Locale } from '@/lib/localePath';
import { stripLocalePrefix, withLocalePrefix } from '@/lib/localePath';
import '@/styles/Home.css';

// 名前からアンダースコア以降をトリムする関数
const POS_P = '\u6295\u624b';
const POS_C = '\u6355\u624b';
const POS_IF = '\u5185\u91ce\u624b';
const POS_OF = '\u5916\u91ce\u624b';
const POS_DH = '\u6307\u540d\u6253\u8005';
const POS_1B = '\u4e00\u5841\u624b';
const POS_2B = '\u4e8c\u5841\u624b';
const POS_3B = '\u4e09\u5841\u624b';
const POS_SS = '\u904a\u6483\u624b';
const POS_ALL_LABEL = '\u3059\u3079\u3066';
const POS_ALL = 'all';
const POSITION_OPTIONS = [POS_P, POS_C, POS_1B, POS_2B, POS_3B, POS_SS, POS_OF, POS_DH] as const;
const DEFAULT_SEARCH_POSITION = POS_ALL;
const PLAYER_SEARCH_PAGE_SIZE = 20;
const NATIONALITY_OPTIONS = [
    { value: '日本', ja: '日本', en: 'Japan', es: 'Japón' },
    { value: 'アメリカ合衆国', ja: 'アメリカ', en: 'USA', es: 'EE.UU.' },
    { value: 'ドミニカ共和国', ja: 'ドミニカ', en: 'Dominican Rep.', es: 'Rep. Dominicana' },
    { value: 'ベネズエラ', ja: 'ベネズエラ', en: 'Venezuela', es: 'Venezuela' },
    { value: 'キューバ', ja: 'キューバ', en: 'Cuba', es: 'Cuba' },
    { value: 'カナダ', ja: 'カナダ', en: 'Canada', es: 'Canadá' },
    { value: 'プエルトリコ', ja: 'プエルトリコ', en: 'Puerto Rico', es: 'Puerto Rico' },
    { value: 'メキシコ', ja: 'メキシコ', en: 'Mexico', es: 'México' },
    { value: 'パナマ', ja: 'パナマ', en: 'Panama', es: 'Panamá' },
    { value: '韓国', ja: '韓国', en: 'South Korea', es: 'Corea del Sur' },
    { value: '台湾', ja: '台湾', en: 'Taiwan', es: 'Taiwán' },
    { value: 'イギリス', ja: 'イギリス', en: 'UK', es: 'Reino Unido' },
    { value: 'ドイツ', ja: 'ドイツ', en: 'Germany', es: 'Alemania' },
    { value: 'オーストラリア', ja: 'オーストラリア', en: 'Australia', es: 'Australia' },
    { value: 'コロンビア', ja: 'コロンビア', en: 'Colombia', es: 'Colombia' },
    { value: 'オランダ', ja: 'オランダ', en: 'Netherlands', es: 'Países Bajos' },
    { value: 'ブラジル', ja: 'ブラジル', en: 'Brazil', es: 'Brasil' },
    { value: 'ニカラグア', ja: 'ニカラグア', en: 'Nicaragua', es: 'Nicaragua' },
];

type RosterStatus = 'active' | 'ob' | 'all';
type CareerYearSelection = number | 'career' | null;
type DefensePositionKey = 'P' | 'C' | '1B' | '2B' | '3B' | 'SS' | 'LF' | 'CF' | 'RF' | 'OF' | 'IF';

const YEAR_FILTER_START = 1855;
const YEAR_FILTER_END = new Date().getFullYear();
const YEAR_FILTER_OPTIONS = Array.from(
    { length: Math.max(0, YEAR_FILTER_END - YEAR_FILTER_START + 1) },
    (_, i) => YEAR_FILTER_END - i
);

const filterYearOptions = (years: number[], keyword: string): number[] => {
    const normalized = keyword.trim();
    if (!normalized) return years;
    return years.filter(y => String(y).includes(normalized));
};

type HomeTab = 'npb' | 'mlb' | 'players';

const parseHomeTab = (value: string | null): HomeTab | null => {
    if (value === 'npb' || value === 'mlb' || value === 'players') return value;
    return null;
};

const getPathHomeTab = (pathname: string): HomeTab | null => {
    const routePath = stripLocalePrefix(pathname);
    if (routePath.startsWith('/npb')) return 'npb';
    if (routePath.startsWith('/mlb')) return 'mlb';
    if (routePath.startsWith('/players')) return 'players';
    return null;
};

type YearComboboxProps = {
    label: string;
    value: number | null;
    onChange: (year: number | null) => void;
    disabled: boolean;
    years: number[];
    anyLabel: string;
    clearLabel: string;
    searchPlaceholder: string;
    noResultLabel: string;
};

const YearCombobox = ({
    label,
    value,
    onChange,
    disabled,
    years,
    anyLabel,
    clearLabel,
    searchPlaceholder,
    noResultLabel,
}: YearComboboxProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const filteredYears = filterYearOptions(years, search);
    const optionValues: Array<number | null> = search.trim() ? filteredYears : [null, ...filteredYears];
    const activeOptionIndex = optionValues.length > 0 ? Math.min(activeIndex, optionValues.length - 1) : -1;

    useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
                setSearch('');
                setActiveIndex(0);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        inputRef.current?.focus();
        inputRef.current?.select();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const activeButton = listRef.current?.querySelector<HTMLButtonElement>('[data-active="true"]');
        activeButton?.scrollIntoView({ block: 'nearest' });
    }, [open, activeOptionIndex]);

    const openMenu = () => {
        if (disabled) return;
        const selectedIndex = value === null ? 0 : Math.max(0, years.indexOf(value) + 1);
        setOpen(true);
        setSearch('');
        setActiveIndex(selectedIndex);
    };

    const closeMenu = () => {
        setOpen(false);
        setSearch('');
        setActiveIndex(0);
    };

    const chooseOption = (next: number | null) => {
        onChange(next);
        closeMenu();
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (optionValues.length === 0) return;
            setActiveIndex(prev => Math.min(prev + 1, optionValues.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (optionValues.length === 0) return;
            setActiveIndex(prev => Math.max(prev - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const target = optionValues[activeOptionIndex];
            if (target !== undefined) {
                chooseOption(target);
                return;
            }
            const typedYear = Number(search.trim());
            if (Number.isInteger(typedYear) && years.includes(typedYear)) {
                chooseOption(typedYear);
            }
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            closeMenu();
        }
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openMenu();
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            const selectedIndex = value === null ? 0 : Math.max(0, years.indexOf(value) + 1);
            setOpen(true);
            setSearch('');
            setActiveIndex(selectedIndex > 0 ? selectedIndex : optionValues.length - 1);
        }
    };

    return (
        <div className="year-filter-row">
            <span className="year-filter-label">{label}</span>
            <div ref={containerRef} className="year-combobox-wrap">
                <div className="year-combobox-trigger-row">
                    <button
                        type="button"
                        className="year-combobox-trigger"
                        onClick={() => (open ? closeMenu() : openMenu())}
                        onKeyDown={handleTriggerKeyDown}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        disabled={disabled}
                    >
                        <span>{value === null ? anyLabel : String(value)}</span>
                        <span className="year-combobox-caret">▼</span>
                    </button>
                    {value !== null && (
                        <button
                            type="button"
                            className="year-combobox-clear"
                            onClick={() => onChange(null)}
                            disabled={disabled}
                        >
                            {clearLabel}
                        </button>
                    )}
                </div>
                {open && (
                    <div className="year-combobox-menu">
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setActiveIndex(0);
                            }}
                            onKeyDown={handleInputKeyDown}
                            placeholder={searchPlaceholder}
                            className="year-combobox-input"
                            inputMode="numeric"
                        />
                        <div className="year-combobox-list" ref={listRef} role="listbox">
                            {optionValues.length === 0 ? (
                                <div className="year-combobox-empty">{noResultLabel}</div>
                            ) : (
                                optionValues.map((option, index) => {
                                    const isSelected = option === value;
                                    const isActive = index === activeOptionIndex;
                                    return (
                                        <button
                                            key={option === null ? 'any' : option}
                                            type="button"
                                            role="option"
                                            aria-selected={isSelected}
                                            data-active={isActive ? 'true' : 'false'}
                                            className={`year-combobox-option${isSelected ? ' is-selected' : ''}${isActive ? ' is-active' : ''}`}
                                            onClick={() => chooseOption(option)}
                                            onMouseEnter={() => setActiveIndex(index)}
                                        >
                                            {option === null ? anyLabel : String(option)}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

type CareerYearComboboxProps = {
    label: string;
    value: CareerYearSelection;
    onChange: (year: CareerYearSelection) => void;
    disabled: boolean;
    years: number[];
    anyLabel: string;
    careerLabel: string;
    clearLabel: string;
    searchPlaceholder: string;
    noResultLabel: string;
};

const filterCareerYearOptions = (
    years: number[],
    keyword: string,
    careerLabel: string
): CareerYearSelection[] => {
    const normalized = keyword.trim().toLowerCase();
    const filteredYears = filterYearOptions(years, keyword);
    if (!normalized) {
        return [null, 'career', ...filteredYears];
    }
    const options: CareerYearSelection[] = [...filteredYears];
    if (careerLabel.toLowerCase().includes(normalized)) {
        options.unshift('career');
    }
    return options;
};

const CareerYearCombobox = ({
    label,
    value,
    onChange,
    disabled,
    years,
    anyLabel,
    careerLabel,
    clearLabel,
    searchPlaceholder,
    noResultLabel,
}: CareerYearComboboxProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const optionValues = filterCareerYearOptions(years, search, careerLabel);
    const activeOptionIndex = optionValues.length > 0 ? Math.min(activeIndex, optionValues.length - 1) : -1;

    useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
                setSearch('');
                setActiveIndex(0);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        inputRef.current?.focus();
        inputRef.current?.select();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const activeButton = listRef.current?.querySelector<HTMLButtonElement>('[data-active="true"]');
        activeButton?.scrollIntoView({ block: 'nearest' });
    }, [open, activeOptionIndex]);

    const openMenu = () => {
        if (disabled) return;
        const defaultValues: CareerYearSelection[] = [null, 'career', ...years];
        const selectedIndex = Math.max(0, defaultValues.findIndex(v => v === value));
        setOpen(true);
        setSearch('');
        setActiveIndex(selectedIndex);
    };

    const closeMenu = () => {
        setOpen(false);
        setSearch('');
        setActiveIndex(0);
    };

    const chooseOption = (next: CareerYearSelection) => {
        onChange(next);
        closeMenu();
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (optionValues.length === 0) return;
            setActiveIndex(prev => Math.min(prev + 1, optionValues.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (optionValues.length === 0) return;
            setActiveIndex(prev => Math.max(prev - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const target = optionValues[activeOptionIndex];
            if (target !== undefined) {
                chooseOption(target);
                return;
            }
            const typedYear = Number(search.trim());
            if (Number.isInteger(typedYear) && years.includes(typedYear)) {
                chooseOption(typedYear);
            }
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            closeMenu();
        }
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openMenu();
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            const defaultValues: CareerYearSelection[] = [null, 'career', ...years];
            const selectedIndex = Math.max(0, defaultValues.findIndex(v => v === value));
            setOpen(true);
            setSearch('');
            setActiveIndex(selectedIndex > 0 ? selectedIndex : defaultValues.length - 1);
        }
    };

    const triggerText = value === null
        ? anyLabel
        : value === 'career'
            ? careerLabel
            : String(value);

    return (
        <div className="year-filter-row">
            <span className="year-filter-label">{label}</span>
            <div ref={containerRef} className="year-combobox-wrap">
                <div className="year-combobox-trigger-row">
                    <button
                        type="button"
                        className="year-combobox-trigger"
                        onClick={() => (open ? closeMenu() : openMenu())}
                        onKeyDown={handleTriggerKeyDown}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        disabled={disabled}
                    >
                        <span>{triggerText}</span>
                        <span className="year-combobox-caret">▼</span>
                    </button>
                    {value !== null && (
                        <button
                            type="button"
                            className="year-combobox-clear"
                            onClick={() => onChange(null)}
                            disabled={disabled}
                        >
                            {clearLabel}
                        </button>
                    )}
                </div>
                {open && (
                    <div className="year-combobox-menu">
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setActiveIndex(0);
                            }}
                            onKeyDown={handleInputKeyDown}
                            placeholder={searchPlaceholder}
                            className="year-combobox-input"
                            inputMode="numeric"
                        />
                        <div className="year-combobox-list" ref={listRef} role="listbox">
                            {optionValues.length === 0 ? (
                                <div className="year-combobox-empty">{noResultLabel}</div>
                            ) : (
                                optionValues.map((option, index) => {
                                    const isSelected = option === value;
                                    const isActive = index === activeOptionIndex;
                                    return (
                                        <button
                                            key={option === null ? 'any' : option}
                                            type="button"
                                            role="option"
                                            aria-selected={isSelected}
                                            data-active={isActive ? 'true' : 'false'}
                                            className={`year-combobox-option${isSelected ? ' is-selected' : ''}${isActive ? ' is-active' : ''}`}
                                            onClick={() => chooseOption(option)}
                                            onMouseEnter={() => setActiveIndex(index)}
                                        >
                                            {option === null ? anyLabel : option === 'career' ? careerLabel : String(option)}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const PITCHER_CAREER_FILTER_DEFS = [
    { key: 'games', ja: '試合数', en: 'Games', es: 'Juegos', step: '1' },
    { key: 'starts', ja: '先発', en: 'Starts', es: 'Aperturas', step: '1' },
    { key: 'complete', ja: '完投', en: 'Complete Games', es: 'Juegos completos', step: '1' },
    { key: 'shutout', ja: '完封', en: 'Shutouts', es: 'Blanqueadas', step: '1' },
    { key: 'no_walk', ja: '無四球', en: 'No-Walk', es: 'Sin boletos', step: '1' },
    { key: 'wins', ja: '勝利', en: 'Wins', es: 'Victorias', step: '1' },
    { key: 'losses', ja: '敗戦', en: 'Losses', es: 'Derrotas', step: '1' },
    { key: 'saves', ja: 'セーブ', en: 'Saves', es: 'Salvamentos', step: '1' },
    { key: 'holds', ja: 'ホールド', en: 'Holds', es: 'Holds', step: '1' },
    { key: 'win_rate', ja: '勝率', en: 'Win %', es: 'Porc. victorias', step: '0.001' },
    { key: 'batters', ja: '打者', en: 'Batters Faced', es: 'Bateadores', step: '1' },
    { key: 'innings', ja: '投球回', en: 'Innings', es: 'Entradas', step: '0.1' },
    { key: 'hits', ja: '被安打', en: 'Hits Allowed', es: 'Hits permitidos', step: '1' },
    { key: 'home_runs', ja: '被本塁打', en: 'HR Allowed', es: 'HR permitidos', step: '1' },
    { key: 'walks', ja: '与四球', en: 'Walks', es: 'Boletos', step: '1' },
    { key: 'intentional_walks', ja: '敬遠', en: 'Intentional Walks', es: 'Boletos intenc.', step: '1' },
    { key: 'hit_by_pitch', ja: '与死球', en: 'Hit Batters', es: 'Pelotazos', step: '1' },
    { key: 'strikeouts', ja: '奪三振', en: 'Strikeouts', es: 'Ponches', step: '1' },
    { key: 'wild_pitches', ja: '暴投', en: 'Wild Pitches', es: 'Lanz. descontrol.', step: '1' },
    { key: 'balks', ja: 'ボーク', en: 'Balks', es: 'Balks', step: '1' },
    { key: 'runs', ja: '失点', en: 'Runs Allowed', es: 'Carreras permitidas', step: '1' },
    { key: 'earned_runs', ja: '自責点', en: 'Earned Runs', es: 'Carreras limpias', step: '1' },
    { key: 'era', ja: '防御率', en: 'ERA', es: 'ERA', step: '0.01' },
    { key: 'whip', ja: 'WHIP', en: 'WHIP', es: 'WHIP', step: '0.01' },
] as const;

const BATTING_CAREER_FILTER_DEFS = [
    { key: 'games', ja: '試合数', en: 'Games', es: 'Juegos', step: '1' },
    { key: 'plate_appearances', ja: '打席数', en: 'Plate Appearances', es: 'Apariciones', step: '1' },
    { key: 'at_bats', ja: '打数', en: 'At Bats', es: 'Turnos', step: '1' },
    { key: 'runs', ja: '得点', en: 'Runs', es: 'Carreras', step: '1' },
    { key: 'hits', ja: '安打', en: 'Hits', es: 'Hits', step: '1' },
    { key: 'doubles', ja: '二塁打', en: 'Doubles', es: 'Dobles', step: '1' },
    { key: 'triples', ja: '三塁打', en: 'Triples', es: 'Triples', step: '1' },
    { key: 'home_runs', ja: '本塁打', en: 'Home Runs', es: 'Jonrones', step: '1' },
    { key: 'total_bases', ja: '塁打', en: 'Total Bases', es: 'Bases totales', step: '1' },
    { key: 'rbi', ja: '打点', en: 'RBI', es: 'Impulsadas', step: '1' },
    { key: 'steals', ja: '盗塁', en: 'Steals', es: 'Robos', step: '1' },
    { key: 'caught_stealing', ja: '盗塁死', en: 'Caught Stealing', es: 'Atrapado robando', step: '1' },
    { key: 'sac_bunts', ja: '犠打', en: 'Sacrifice Bunts', es: 'Toques de sacrificio', step: '1' },
    { key: 'sac_flies', ja: '犠飛', en: 'Sacrifice Flies', es: 'Elevados de sacrificio', step: '1' },
    { key: 'walks', ja: '四球', en: 'Walks', es: 'Boletos', step: '1' },
    { key: 'intentional_walks', ja: '敬遠', en: 'Intentional Walks', es: 'Boletos intenc.', step: '1' },
    { key: 'hit_by_pitch', ja: '死球', en: 'Hit By Pitch', es: 'Golpeado por lanzamiento', step: '1' },
    { key: 'strikeouts', ja: '三振', en: 'Strikeouts', es: 'Ponches', step: '1' },
    { key: 'double_plays', ja: '併殺打', en: 'Grounded Into DP', es: 'Doble play bateando', step: '1' },
    { key: 'avg', ja: '打率', en: 'AVG', es: 'AVG', step: '0.001' },
    { key: 'obp', ja: '出塁率', en: 'OBP', es: 'OBP', step: '0.001' },
    { key: 'slg', ja: '長打率', en: 'SLG', es: 'SLG', step: '0.001' },
    { key: 'ops', ja: 'OPS', en: 'OPS', es: 'OPS', step: '0.001' },
] as const;

const DEFENSE_CAREER_FILTER_DEFS = [
    { key: 'games', ja: '試合', en: 'Games', es: 'Juegos', step: '1' },
    { key: 'putouts', ja: '刺殺', en: 'Putouts', es: 'Outs', step: '1' },
    { key: 'assists', ja: '補殺', en: 'Assists', es: 'Asistencias', step: '1' },
    { key: 'errors', ja: '失策', en: 'Errors', es: 'Errores', step: '1' },
    { key: 'double_plays', ja: '併殺', en: 'Double Plays', es: 'Dobles matanzas', step: '1' },
    { key: 'fielding_percentage', ja: '守備率', en: 'Fielding %', es: 'Porc. fildeo', step: '0.001' },
] as const;

type PitcherCareerFilterKey = (typeof PITCHER_CAREER_FILTER_DEFS)[number]['key'];
type PitcherCareerDraftFilters = Record<PitcherCareerFilterKey, { op: PitcherCareerComparisonOperator; value: string }>;
type PitcherCareerAppliedFilters = Partial<Record<PitcherCareerFilterKey, PitcherCareerFilterCondition>>;
type BattingCareerFilterKey = (typeof BATTING_CAREER_FILTER_DEFS)[number]['key'];
type BattingCareerDraftFilters = Record<BattingCareerFilterKey, { op: PitcherCareerComparisonOperator; value: string }>;
type BattingCareerAppliedFilters = Partial<Record<BattingCareerFilterKey, PitcherCareerFilterCondition>>;
type DefenseCareerFilterKey = (typeof DEFENSE_CAREER_FILTER_DEFS)[number]['key'];
type DefenseCareerDraftFilters = Record<DefenseCareerFilterKey, { op: PitcherCareerComparisonOperator; value: string }>;
type DefenseCareerAppliedFilters = Partial<Record<DefenseCareerFilterKey, PitcherCareerFilterCondition>>;
type PitcherSeasonDraftFilters = PitcherCareerDraftFilters;
type PitcherSeasonAppliedFilters = PitcherCareerAppliedFilters;
type BattingSeasonDraftFilters = BattingCareerDraftFilters;
type BattingSeasonAppliedFilters = BattingCareerAppliedFilters;
type DefenseSeasonDraftFilters = DefenseCareerDraftFilters;
type DefenseSeasonAppliedFilters = DefenseCareerAppliedFilters;
type YearModalTarget = 'first' | 'last' | 'pitcher_season' | 'batter_season' | 'defense_season';
type DefenseSearchMode = DefenseFilterScope;

const DEFENSE_POSITION_OPTIONS: Array<{ value: DefensePositionKey; ja: string; en: string; es: string }> = [
    { value: 'P', ja: '投手', en: 'Pitcher', es: 'Lanzador' },
    { value: 'C', ja: '捕手', en: 'Catcher', es: 'Receptor' },
    { value: '1B', ja: '一塁手', en: '1B', es: '1B' },
    { value: '2B', ja: '二塁手', en: '2B', es: '2B' },
    { value: '3B', ja: '三塁手', en: '3B', es: '3B' },
    { value: 'SS', ja: '遊撃手', en: 'SS', es: 'SS' },
    { value: 'LF', ja: '左翼手', en: 'LF', es: 'LF' },
    { value: 'CF', ja: '中堅手', en: 'CF', es: 'CF' },
    { value: 'RF', ja: '右翼手', en: 'RF', es: 'RF' },
    { value: 'OF', ja: '外野手', en: 'OF', es: 'OF' },
    { value: 'IF', ja: '内野手', en: 'IF', es: 'IF' },
];

const createPitcherCareerDraftFilters = (): PitcherCareerDraftFilters => {
    const state = {} as PitcherCareerDraftFilters;
    for (const stat of PITCHER_CAREER_FILTER_DEFS) {
        state[stat.key] = { op: 'gte', value: '' };
    }
    return state;
};

const buildPitcherCareerAppliedFilters = (draft: PitcherCareerDraftFilters): PitcherCareerAppliedFilters => {
    const applied: PitcherCareerAppliedFilters = {};
    for (const stat of PITCHER_CAREER_FILTER_DEFS) {
        const raw = (draft[stat.key]?.value || '').replace(/,/g, '').trim();
        if (!raw) continue;
        const numericValue = Number(raw);
        if (!Number.isFinite(numericValue)) continue;
        applied[stat.key] = {
            op: draft[stat.key].op === 'lte' ? 'lte' : 'gte',
            value: numericValue,
        };
    }
    return applied;
};

const serializePitcherCareerAppliedFilters = (applied: PitcherCareerAppliedFilters): string => {
    return Object.entries(applied)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, cond]) => `${key}:${cond?.op ?? 'gte'}:${cond?.value ?? ''}`)
        .join('|');
};

const createBattingCareerDraftFilters = (): BattingCareerDraftFilters => {
    const state = {} as BattingCareerDraftFilters;
    for (const stat of BATTING_CAREER_FILTER_DEFS) {
        state[stat.key] = { op: 'gte', value: '' };
    }
    return state;
};

const buildBattingCareerAppliedFilters = (draft: BattingCareerDraftFilters): BattingCareerAppliedFilters => {
    const applied: BattingCareerAppliedFilters = {};
    for (const stat of BATTING_CAREER_FILTER_DEFS) {
        const raw = (draft[stat.key]?.value || '').replace(/,/g, '').trim();
        if (!raw) continue;
        const numericValue = Number(raw);
        if (!Number.isFinite(numericValue)) continue;
        applied[stat.key] = {
            op: draft[stat.key].op === 'lte' ? 'lte' : 'gte',
            value: numericValue,
        };
    }
    return applied;
};

const serializeBattingCareerAppliedFilters = (applied: BattingCareerAppliedFilters): string => {
    return Object.entries(applied)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, cond]) => `${key}:${cond?.op ?? 'gte'}:${cond?.value ?? ''}`)
        .join('|');
};

const createDefenseCareerDraftFilters = (): DefenseCareerDraftFilters => {
    const state = {} as DefenseCareerDraftFilters;
    for (const stat of DEFENSE_CAREER_FILTER_DEFS) {
        state[stat.key] = { op: 'gte', value: '' };
    }
    return state;
};

const buildDefenseCareerAppliedFilters = (draft: DefenseCareerDraftFilters): DefenseCareerAppliedFilters => {
    const applied: DefenseCareerAppliedFilters = {};
    for (const stat of DEFENSE_CAREER_FILTER_DEFS) {
        const raw = (draft[stat.key]?.value || '').replace(/,/g, '').trim();
        if (!raw) continue;
        const numericValue = Number(raw);
        if (!Number.isFinite(numericValue)) continue;
        applied[stat.key] = {
            op: draft[stat.key].op === 'lte' ? 'lte' : 'gte',
            value: numericValue,
        };
    }
    return applied;
};

const serializeDefenseCareerAppliedFilters = (applied: DefenseCareerAppliedFilters): string => {
    return Object.entries(applied)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, cond]) => `${key}:${cond?.op ?? 'gte'}:${cond?.value ?? ''}`)
        .join('|');
};

const getCareerValuePlaceholder = (
    t: (ja: string, en: string, es?: string) => string,
    statKey: string,
    step: string
): string => {
    if (statKey === 'innings') {
        return t('例: 120.1', 'e.g. 120.1', 'ej.: 120.1');
    }
    if (statKey === 'fielding_percentage') {
        return t('例: 0.985', 'e.g. 0.985', 'ej.: 0.985');
    }
    if (step === '0.001') {
        return t('例: 0.300', 'e.g. 0.300', 'ej.: 0.300');
    }
    if (step === '0.01') {
        return t('例: 2.50', 'e.g. 2.50', 'ej.: 2.50');
    }
    if (step === '0.1') {
        return t('例: 120.1', 'e.g. 120.1', 'ej.: 120.1');
    }
    return t('例: 30', 'e.g. 30', 'ej.: 30');
};

type HomeSearchParamValues = {
    tab: string | null;
    q: string | null;
    pos: string | null;
    active: string | null;
    ob: string | null;
    team: string | null;
    page: string | null;
};
const DEFAULT_HOME_SPV: HomeSearchParamValues = {
    tab: null, q: null, pos: null, active: null, ob: null, team: null, page: null,
};

const ADVANCED_FILTER_KEY = 'player-search-adv-filters';

function saveAdvancedFilters(data: object): void {
    try { sessionStorage.setItem(ADVANCED_FILTER_KEY, JSON.stringify(data)); } catch {}
}

function loadSavedAdvancedFilters(): Record<string, unknown> | null {
    try {
        const raw = sessionStorage.getItem(ADVANCED_FILTER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function clearAdvancedFilters(): void {
    try { sessionStorage.removeItem(ADVANCED_FILTER_KEY); } catch {}
}

type HomeProps = {
    locale: Locale;
    forcedTab?: HomeTab;
    initialNpbTeams?: Team[];
    initialMlbTeams?: MlbTeam[];
    spv: HomeSearchParamValues;
};

const HomeInner = ({ locale, forcedTab, initialNpbTeams, initialMlbTeams, spv }: HomeProps) => {
    const [teams, setTeams] = useState<Team[]>(initialNpbTeams ?? []);
    const [mlbTeams, setMlbTeams] = useState<MlbTeam[]>(initialMlbTeams ?? []);
    const [loading, setLoading] = useState(false);
    const [isTeamRoutePending, setIsTeamRoutePending] = useState(false);
    const router = useRouter();
    const pathname = usePathname();

    // URLクエリパラメータまたはlocalStorageでタブ状態を管理
    const savedTab = typeof window !== 'undefined' ? parseHomeTab(localStorage.getItem('lastActiveTab')) : null;
    const queryTab = parseHomeTab(spv.tab);
    const pathTab = getPathHomeTab(pathname ?? '');
    const activeTab: HomeTab = forcedTab || pathTab || queryTab || savedTab || 'npb';
    const isPathPlayersRoute = forcedTab === 'players' || pathTab === 'players';

    // 検索クエリはローカルステートで管理（入力中の再レンダリングを防ぐ）
    const urlQuery = spv.q || '';
    const posParam = spv.pos;
    const activeParam = spv.active;
    const obParam = spv.ob;
    const teamParam = spv.team;
    const pageParam = spv.page;

    const initialPosition = posParam || DEFAULT_SEARCH_POSITION;
    const initialPage = (() => {
        const parsed = Number(pageParam);
        if (Number.isFinite(parsed) && parsed >= 1) return Math.floor(parsed);
        return 1;
    })();
    let initialRosterStatus: RosterStatus = 'all';
    if (activeParam === '1' && obParam !== '1') initialRosterStatus = 'active';
    else if (activeParam === '0' && obParam === '1') initialRosterStatus = 'ob';

    // モバイルのback navigation時にsessionStorageから詳細フィルター状態を復元する
    const hasSearchParams = !!(spv.q || spv.pos || spv.active || spv.ob || spv.team || spv.page);
    const savedAdvanced = hasSearchParams ? loadSavedAdvancedFilters() : null;

    const [playerQuery, setPlayerQuery] = useState(urlQuery);
    const [playerResults, setPlayerResults] = useState<PlayerSearchItem[]>([]);
    const [playerTotal, setPlayerTotal] = useState(0);
    const [playerPage, setPlayerPage] = useState(initialPage);
    const [playerSearching, setPlayerSearching] = useState(false);
    const [rosterStatus, setRosterStatus] = useState<RosterStatus>(initialRosterStatus);
    const [selectedPosition, setSelectedPosition] = useState<string>(initialPosition);
    const sa = savedAdvanced;
    const [selectedPitch, setSelectedPitch] = useState<string>((sa?.selectedPitch as string) ?? 'all');
    const [appliedPitch, setAppliedPitch] = useState<string>((sa?.appliedPitch as string) ?? 'all');
    const [selectedBat, setSelectedBat] = useState<string>((sa?.selectedBat as string) ?? 'all');
    const [appliedBat, setAppliedBat] = useState<string>((sa?.appliedBat as string) ?? 'all');
    const [selectedNationality, setSelectedNationality] = useState<string[]>(
        Array.isArray(sa?.selectedNationality) ? (sa.selectedNationality as string[]) : []
    );
    const [appliedNationality, setAppliedNationality] = useState<string[]>(
        Array.isArray(sa?.appliedNationality) ? (sa.appliedNationality as string[]) : []
    );
    const [selectedFirstYear, setSelectedFirstYear] = useState<number | null>(
        typeof sa?.selectedFirstYear === 'number' ? sa.selectedFirstYear : null
    );
    const [appliedFirstYear, setAppliedFirstYear] = useState<number | null>(
        typeof sa?.appliedFirstYear === 'number' ? sa.appliedFirstYear : null
    );
    const [selectedLastYear, setSelectedLastYear] = useState<number | null>(
        typeof sa?.selectedLastYear === 'number' ? sa.selectedLastYear : null
    );
    const [appliedLastYear, setAppliedLastYear] = useState<number | null>(
        typeof sa?.appliedLastYear === 'number' ? sa.appliedLastYear : null
    );
    const [selectedPitcherCareerFilters, setSelectedPitcherCareerFilters] = useState<PitcherCareerDraftFilters>(() => ({
        ...createPitcherCareerDraftFilters(),
        ...(sa?.selectedPitcherCareerFilters as PitcherCareerDraftFilters | undefined),
    }));
    const [appliedPitcherCareerFilters, setAppliedPitcherCareerFilters] = useState<PitcherCareerAppliedFilters>(
        (sa?.appliedPitcherCareerFilters as PitcherCareerAppliedFilters) ?? {}
    );
    const [selectedPitcherSeasonYear, setSelectedPitcherSeasonYear] = useState<CareerYearSelection>((() => {
        const v = sa?.selectedPitcherSeasonYear;
        if (v === 'career' || v === null || typeof v === 'number') return v as CareerYearSelection;
        return 'career';
    })());
    const [appliedPitcherSeasonYear, setAppliedPitcherSeasonYear] = useState<number | null>(
        typeof sa?.appliedPitcherSeasonYear === 'number' ? sa.appliedPitcherSeasonYear : null
    );
    const [selectedPitcherSeasonFilters, setSelectedPitcherSeasonFilters] = useState<PitcherSeasonDraftFilters>(() => ({
        ...createPitcherCareerDraftFilters(),
        ...(sa?.selectedPitcherSeasonFilters as PitcherSeasonDraftFilters | undefined),
    }));
    const [appliedPitcherSeasonFilters, setAppliedPitcherSeasonFilters] = useState<PitcherSeasonAppliedFilters>(
        (sa?.appliedPitcherSeasonFilters as PitcherSeasonAppliedFilters) ?? {}
    );
    const [selectedBattingCareerFilters, setSelectedBattingCareerFilters] = useState<BattingCareerDraftFilters>(() => ({
        ...createBattingCareerDraftFilters(),
        ...(sa?.selectedBattingCareerFilters as BattingCareerDraftFilters | undefined),
    }));
    const [appliedBattingCareerFilters, setAppliedBattingCareerFilters] = useState<BattingCareerAppliedFilters>(
        (sa?.appliedBattingCareerFilters as BattingCareerAppliedFilters) ?? {}
    );
    const [selectedBattingSeasonYear, setSelectedBattingSeasonYear] = useState<CareerYearSelection>((() => {
        const v = sa?.selectedBattingSeasonYear;
        if (v === 'career' || v === null || typeof v === 'number') return v as CareerYearSelection;
        return 'career';
    })());
    const [appliedBattingSeasonYear, setAppliedBattingSeasonYear] = useState<number | null>(
        typeof sa?.appliedBattingSeasonYear === 'number' ? sa.appliedBattingSeasonYear : null
    );
    const [selectedBattingSeasonFilters, setSelectedBattingSeasonFilters] = useState<BattingSeasonDraftFilters>(() => ({
        ...createBattingCareerDraftFilters(),
        ...(sa?.selectedBattingSeasonFilters as BattingSeasonDraftFilters | undefined),
    }));
    const [appliedBattingSeasonFilters, setAppliedBattingSeasonFilters] = useState<BattingSeasonAppliedFilters>(
        (sa?.appliedBattingSeasonFilters as BattingSeasonAppliedFilters) ?? {}
    );
    const [selectedDefenseCareerFilters, setSelectedDefenseCareerFilters] = useState<DefenseCareerDraftFilters>(() => ({
        ...createDefenseCareerDraftFilters(),
        ...(sa?.selectedDefenseCareerFilters as DefenseCareerDraftFilters | undefined),
    }));
    const [appliedDefenseCareerFilters, setAppliedDefenseCareerFilters] = useState<DefenseCareerAppliedFilters>(
        (sa?.appliedDefenseCareerFilters as DefenseCareerAppliedFilters) ?? {}
    );
    const [selectedDefenseSearchMode, setSelectedDefenseSearchMode] = useState<DefenseSearchMode>(
        (sa?.selectedDefenseSearchMode as DefenseSearchMode) ?? 'total'
    );
    const [appliedDefenseSearchMode, setAppliedDefenseSearchMode] = useState<DefenseSearchMode>(
        (sa?.appliedDefenseSearchMode as DefenseSearchMode) ?? 'total'
    );
    const [selectedDefensePosition, setSelectedDefensePosition] = useState<DefensePositionKey | null>(
        (sa?.selectedDefensePosition as DefensePositionKey | null) ?? null
    );
    const [appliedDefensePosition, setAppliedDefensePosition] = useState<DefensePositionKey | null>(
        (sa?.appliedDefensePosition as DefensePositionKey | null) ?? null
    );
    const [selectedDefenseSeasonYear, setSelectedDefenseSeasonYear] = useState<CareerYearSelection>((() => {
        const v = sa?.selectedDefenseSeasonYear;
        if (v === 'career' || v === null || typeof v === 'number') return v as CareerYearSelection;
        return 'career';
    })());
    const [appliedDefenseSeasonYear, setAppliedDefenseSeasonYear] = useState<number | null>(
        typeof sa?.appliedDefenseSeasonYear === 'number' ? sa.appliedDefenseSeasonYear : null
    );
    const [selectedDefenseSeasonFilters, setSelectedDefenseSeasonFilters] = useState<DefenseSeasonDraftFilters>(() => ({
        ...createDefenseCareerDraftFilters(),
        ...(sa?.selectedDefenseSeasonFilters as DefenseSeasonDraftFilters | undefined),
    }));
    const [appliedDefenseSeasonFilters, setAppliedDefenseSeasonFilters] = useState<DefenseSeasonAppliedFilters>(
        (sa?.appliedDefenseSeasonFilters as DefenseSeasonAppliedFilters) ?? {}
    );
    const [selectedTeam, setSelectedTeam] = useState<string[]>(() => {
        const tp = teamParam;
        if (!tp || tp === 'all') return [];
        return tp.split(',').map(s => s.trim()).filter(Boolean);
    });
    const [appliedTeam, setAppliedTeam] = useState<string[]>(() => {
        const tp = teamParam;
        if (!tp || tp === 'all') return [];
        return tp.split(',').map(s => s.trim()).filter(Boolean);
    });
    const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(sa?.showAdvancedFilters === true);
    const [showNpbTeamModal, setShowNpbTeamModal] = useState<boolean>(false);
    const [showMlbTeamModal, setShowMlbTeamModal] = useState<boolean>(false);
    const [showNationalityModal, setShowNationalityModal] = useState<boolean>(false);
    const [showYearModal, setShowYearModal] = useState<YearModalTarget | null>(null);
    const [showCoreAdvancedFilters, setShowCoreAdvancedFilters] = useState<boolean>(sa?.showCoreAdvancedFilters === true);
    const [showPitcherCareerFilters, setShowPitcherCareerFilters] = useState<boolean>(sa?.showPitcherCareerFilters === true);
    const [showBattingCareerFilters, setShowBattingCareerFilters] = useState<boolean>(sa?.showBattingCareerFilters === true);
    const [showDefenseCareerFilters, setShowDefenseCareerFilters] = useState<boolean>(sa?.showDefenseCareerFilters === true);
    const [mobileYearSearch, setMobileYearSearch] = useState<string>('');
    const [manualSearchTrigger, setManualSearchTrigger] = useState<number>(0);
    const lastTeamParamRef = useRef<string>((teamParam && teamParam !== 'all') ? teamParam : 'all');
    const lastManualSearchTriggerRef = useRef<number>(0);
    const [suppressInlineSearchStatus, setSuppressInlineSearchStatus] = useState(false);
    const [isPaging, setIsPaging] = useState(false);
    const lastSearchSignatureRef = useRef<string>('');
    const lastSearchPageRef = useRef<number>(initialPage);

    // 「選手検索」タブクリック時にフィルター・検索結果をリセット
    useEffect(() => {
        const handleReset = () => {
            setSelectedPitch('all');
            setAppliedPitch('all');
            setSelectedBat('all');
            setAppliedBat('all');
            setSelectedNationality([]);
            setAppliedNationality([]);
            setSelectedFirstYear(null);
            setAppliedFirstYear(null);
            setSelectedLastYear(null);
            setAppliedLastYear(null);
            setSelectedPitcherCareerFilters(createPitcherCareerDraftFilters());
            setAppliedPitcherCareerFilters({});
            setSelectedPitcherSeasonYear('career');
            setAppliedPitcherSeasonYear(null);
            setSelectedPitcherSeasonFilters(createPitcherCareerDraftFilters());
            setAppliedPitcherSeasonFilters({});
            setSelectedBattingCareerFilters(createBattingCareerDraftFilters());
            setAppliedBattingCareerFilters({});
            setSelectedBattingSeasonYear('career');
            setAppliedBattingSeasonYear(null);
            setSelectedBattingSeasonFilters(createBattingCareerDraftFilters());
            setAppliedBattingSeasonFilters({});
            setSelectedDefenseCareerFilters(createDefenseCareerDraftFilters());
            setAppliedDefenseCareerFilters({});
            setSelectedDefenseSearchMode('total');
            setAppliedDefenseSearchMode('total');
            setSelectedDefensePosition(null);
            setAppliedDefensePosition(null);
            setSelectedDefenseSeasonYear('career');
            setAppliedDefenseSeasonYear(null);
            setSelectedDefenseSeasonFilters(createDefenseCareerDraftFilters());
            setAppliedDefenseSeasonFilters({});
            setShowCoreAdvancedFilters(false);
            setShowPitcherCareerFilters(false);
            setShowBattingCareerFilters(false);
            setShowDefenseCareerFilters(false);
            setSelectedTeam([]);
            setAppliedTeam([]);
            setShowAdvancedFilters(false);
            setShowYearModal(null);
            setMobileYearSearch('');
            setPlayerResults([]);
            setPlayerTotal(0);
            setPlayerPage(1);
            setManualSearchTrigger(0);
            setPlayerQuery('');
            clearAdvancedFilters();
        };
        window.addEventListener('resetPlayerFilters', handleReset);
        return () => window.removeEventListener('resetPlayerFilters', handleReset);
    }, []);

    // 詳細フィルター状態をsessionStorageに保存（モバイルのback navigation対応）
    useEffect(() => {
        saveAdvancedFilters({
            showAdvancedFilters, showCoreAdvancedFilters, showPitcherCareerFilters,
            showBattingCareerFilters, showDefenseCareerFilters,
            selectedPitch, appliedPitch,
            selectedBat, appliedBat,
            selectedNationality, appliedNationality,
            selectedFirstYear, appliedFirstYear,
            selectedLastYear, appliedLastYear,
            selectedPitcherCareerFilters, appliedPitcherCareerFilters,
            selectedPitcherSeasonYear, appliedPitcherSeasonYear,
            selectedPitcherSeasonFilters, appliedPitcherSeasonFilters,
            selectedBattingCareerFilters, appliedBattingCareerFilters,
            selectedBattingSeasonYear, appliedBattingSeasonYear,
            selectedBattingSeasonFilters, appliedBattingSeasonFilters,
            selectedDefenseCareerFilters, appliedDefenseCareerFilters,
            selectedDefenseSearchMode, appliedDefenseSearchMode,
            selectedDefensePosition, appliedDefensePosition,
            selectedDefenseSeasonYear, appliedDefenseSeasonYear,
            selectedDefenseSeasonFilters, appliedDefenseSeasonFilters,
        });
    }, [
        showAdvancedFilters, showCoreAdvancedFilters, showPitcherCareerFilters,
        showBattingCareerFilters, showDefenseCareerFilters,
        selectedPitch, appliedPitch, selectedBat, appliedBat,
        selectedNationality, appliedNationality,
        selectedFirstYear, appliedFirstYear, selectedLastYear, appliedLastYear,
        selectedPitcherCareerFilters, appliedPitcherCareerFilters,
        selectedPitcherSeasonYear, appliedPitcherSeasonYear,
        selectedPitcherSeasonFilters, appliedPitcherSeasonFilters,
        selectedBattingCareerFilters, appliedBattingCareerFilters,
        selectedBattingSeasonYear, appliedBattingSeasonYear,
        selectedBattingSeasonFilters, appliedBattingSeasonFilters,
        selectedDefenseCareerFilters, appliedDefenseCareerFilters,
        selectedDefenseSearchMode, appliedDefenseSearchMode,
        selectedDefensePosition, appliedDefensePosition,
        selectedDefenseSeasonYear, appliedDefenseSeasonYear,
        selectedDefenseSeasonFilters, appliedDefenseSeasonFilters,
    ]);

    // IME変換中フラグ
    const [isComposing, setIsComposing] = useState(false);

    const t = (ja: string, en: string, es?: string) => {
        if (locale === 'ja') return ja;
        if (locale === 'es') return es || en;
        return en;
    };
    const toLocalePath = (path: string) => withLocalePrefix(locale, path);

    const val = (ja: string | null | undefined, en: string | null | undefined) => {
        if (locale === 'en' && en) return en;
        if (locale === 'es' && en) return en; // 選手名などはスペイン語でも英語表記を流用
        if (ja) return ja;
        return en || '-'; // Fallback
    };
    const valStrict = (ja: string | null | undefined, en: string | null | undefined) => {
        if (locale === 'en') return en || '-';
        return ja || '-';
    };
    const getPositionShortLabel = (position: string) => {
        if (position === POS_P) return 'P';
        if (position === POS_C) return 'C';
        if (position === POS_IF) return 'IF';
        if (position === POS_1B) return '1B';
        if (position === POS_2B) return '2B';
        if (position === POS_3B) return '3B';
        if (position === POS_SS) return 'SS';
        if (position === POS_OF) return 'OF';
        return 'DH';
    };

    const teamNameJaBySlug: Record<string, string> = {
        yomiuri_giants: '読売ジャイアンツ',
        hanshin_tigers: '阪神タイガース',
        chunichi_dragons: '中日ドラゴンズ',
        hiroshima_toyo_carp: '広島東洋カープ',
        tokyo_yakult_swallows: '東京ヤクルトスワローズ',
        yokohama_dena_baystars: '横浜DeNAベイスターズ',
        fukuoka_softbank_hawks: '福岡ソフトバンクホークス',
        hokkaido_nipponham_fighters: '北海道日本ハムファイターズ',
        chiba_lotte_marines: '千葉ロッテマリーンズ',
        saitama_seibu_lions: '埼玉西武ライオンズ',
        orix_buffaloes: 'オリックス・バファローズ',
        tohoku_rakuten_golden_eagles: '東北楽天ゴールデンイーグルス',
    };

    const npbTeamNamesEn: Record<string, string> = {
        yomiuri_giants: 'Yomiuri Giants',
        hanshin_tigers: 'Hanshin Tigers',
        chunichi_dragons: 'Chunichi Dragons',
        hiroshima_toyo_carp: 'Hiroshima Toyo Carp',
        tokyo_yakult_swallows: 'Tokyo Yakult Swallows',
        yokohama_dena_baystars: 'Yokohama DeNA BayStars',
        fukuoka_softbank_hawks: 'Fukuoka SoftBank Hawks',
        hokkaido_nipponham_fighters: 'Hokkaido Nippon-Ham Fighters',
        chiba_lotte_marines: 'Chiba Lotte Marines',
        saitama_seibu_lions: 'Saitama Seibu Lions',
        orix_buffaloes: 'Orix Buffaloes',
        tohoku_rakuten_golden_eagles: 'Tohoku Rakuten Golden Eagles',
    };

    const mlbTeamNamesJa: Record<string, string> = {
        arizona_diamondbacks: 'アリゾナ・ダイヤモンドバックス',
        atlanta_braves: 'アトランタ・ブレーブス',
        baltimore_orioles: 'ボルチモア・オリオールズ',
        boston_red_sox: 'ボストン・レッドソックス',
        chicago_cubs: 'シカゴ・カブス',
        chicago_white_sox: 'シカゴ・ホワイトソックス',
        cincinnati_reds: 'シンシナティ・レッズ',
        cleveland_guardians: 'クリーブランド・ガーディアンズ',
        colorado_rockies: 'コロラド・ロッキーズ',
        detroit_tigers: 'デトロイト・タイガース',
        houston_astros: 'ヒューストン・アストロズ',
        kansas_city_royals: 'カンザスシティ・ロイヤルズ',
        los_angeles_angels: 'ロサンゼルス・エンゼルス',
        los_angeles_dodgers: 'ロサンゼルス・ドジャース',
        miami_marlins: 'マイアミ・マーリンズ',
        milwaukee_brewers: 'ミルウォーキー・ブルワーズ',
        minnesota_twins: 'ミネソタ・ツインズ',
        new_york_mets: 'ニューヨーク・メッツ',
        new_york_yankees: 'ニューヨーク・ヤンキース',
        oakland_athletics: 'オークランド・アスレチックス',
        philadelphia_phillies: 'フィラデルフィア・フィリーズ',
        pittsburgh_pirates: 'ピッツバーグ・パイレーツ',
        san_diego_padres: 'サンディエゴ・パドレス',
        san_francisco_giants: 'サンフランシスコ・ジャイアンツ',
        seattle_mariners: 'シアトル・マリナーズ',
        st_louis_cardinals: 'セントルイス・カージナルス',
        tampa_bay_rays: 'タンパベイ・レイズ',
        texas_rangers: 'テキサス・レンジャーズ',
        toronto_blue_jays: 'トロント・ブルージェイズ',
        washington_nationals: 'ワシントン・ナショナルズ',
    };

    const mlbTeamNamesEn: Record<string, string> = {
        arizona_diamondbacks: 'Arizona Diamondbacks',
        atlanta_braves: 'Atlanta Braves',
        baltimore_orioles: 'Baltimore Orioles',
        boston_red_sox: 'Boston Red Sox',
        chicago_cubs: 'Chicago Cubs',
        chicago_white_sox: 'Chicago White Sox',
        cincinnati_reds: 'Cincinnati Reds',
        cleveland_guardians: 'Cleveland Guardians',
        colorado_rockies: 'Colorado Rockies',
        detroit_tigers: 'Detroit Tigers',
        houston_astros: 'Houston Astros',
        kansas_city_royals: 'Kansas City Royals',
        los_angeles_angels: 'Los Angeles Angels',
        los_angeles_dodgers: 'Los Angeles Dodgers',
        miami_marlins: 'Miami Marlins',
        milwaukee_brewers: 'Milwaukee Brewers',
        minnesota_twins: 'Minnesota Twins',
        new_york_mets: 'New York Mets',
        new_york_yankees: 'New York Yankees',
        oakland_athletics: 'Oakland Athletics',
        philadelphia_phillies: 'Philadelphia Phillies',
        pittsburgh_pirates: 'Pittsburgh Pirates',
        san_diego_padres: 'San Diego Padres',
        san_francisco_giants: 'San Francisco Giants',
        seattle_mariners: 'Seattle Mariners',
        st_louis_cardinals: 'St. Louis Cardinals',
        tampa_bay_rays: 'Tampa Bay Rays',
        texas_rangers: 'Texas Rangers',
        toronto_blue_jays: 'Toronto Blue Jays',
        washington_nationals: 'Washington Nationals',
    };

    const mlbTeamNamesEs: Record<string, string> = {
        arizona_diamondbacks: 'Diamondbacks de Arizona',
        atlanta_braves: 'Bravos de Atlanta',
        baltimore_orioles: 'Orioles de Baltimore',
        boston_red_sox: 'Medias Rojas de Boston',
        chicago_cubs: 'Cachorros de Chicago',
        chicago_white_sox: 'Medias Blancas de Chicago',
        cincinnati_reds: 'Rojos de Cincinnati',
        cleveland_guardians: 'Guardianes de Cleveland',
        colorado_rockies: 'Rockies de Colorado',
        detroit_tigers: 'Tigres de Detroit',
        houston_astros: 'Astros de Houston',
        kansas_city_royals: 'Reales de Kansas City',
        los_angeles_angels: 'Angelinos de Los Ángeles',
        los_angeles_dodgers: 'Dodgers de Los Ángeles',
        miami_marlins: 'Marlins de Miami',
        milwaukee_brewers: 'Cerveceros de Milwaukee',
        minnesota_twins: 'Mellizos de Minnesota',
        new_york_mets: 'Mets de Nueva York',
        new_york_yankees: 'Yankees de Nueva York',
        oakland_athletics: 'Atléticos de Oakland',
        philadelphia_phillies: 'Filis de Filadelfia',
        pittsburgh_pirates: 'Piratas de Pittsburgh',
        san_diego_padres: 'Padres de San Diego',
        san_francisco_giants: 'Gigantes de San Francisco',
        seattle_mariners: 'Marineros de Seattle',
        st_louis_cardinals: 'Cardenales de San Luis',
        tampa_bay_rays: 'Rays de Tampa Bay',
        texas_rangers: 'Rangers de Texas',
        toronto_blue_jays: 'Azulejos de Toronto',
        washington_nationals: 'Nacionales de Washington',
    };

    // Helper to get localized team name
    const getTeamName = (slug: string, defaultName: string) => {
        if (locale === 'ja') {
            return teamNameJaBySlug[slug] || mlbTeamNamesJa[slug] || defaultName;
        } else if (locale === 'es') {
            return mlbTeamNamesEs[slug] || npbTeamNamesEn[slug] || teamNameJaBySlug[slug] || defaultName;
        } else {
            return npbTeamNamesEn[slug] || mlbTeamNamesEn[slug] || teamNameJaBySlug[slug] || defaultName;
        }
    };

    // モーダル表示時にbodyスクロールを無効化
    useEffect(() => {
        if (showNpbTeamModal || showMlbTeamModal || showNationalityModal || showYearModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showNpbTeamModal, showMlbTeamModal, showNationalityModal, showYearModal]);

    useEffect(() => {
        if (showYearModal) {
            setMobileYearSearch('');
        }
    }, [showYearModal]);

    // URLからの検索クエリが変わったらローカルステートを更新
    useEffect(() => {
        setPlayerQuery(spv.q || '');

        const act = spv.active;
        const ob = spv.ob;
        if (act === '1' && ob !== '1') setRosterStatus('active');
        else if (act === '0' && ob === '1') setRosterStatus('ob');
        else setRosterStatus('all');

        setSelectedPosition(spv.pos || DEFAULT_SEARCH_POSITION);
        const pageValue = Number(spv.page);
        if (Number.isFinite(pageValue) && pageValue >= 1) {
            setPlayerPage(Math.floor(pageValue));
        } else {
            setPlayerPage(1);
        }
        const tp = spv.team;
        const normalizedTeamParam = (!tp || tp === 'all') ? 'all' : tp;
        if (normalizedTeamParam !== lastTeamParamRef.current) {
            lastTeamParamRef.current = normalizedTeamParam;
            if (normalizedTeamParam === 'all') {
                setSelectedTeam([]);
                setAppliedTeam([]);
            } else {
                const teamsFromParams = normalizedTeamParam.split(',').map(s => s.trim()).filter(Boolean);
                setSelectedTeam(teamsFromParams);
                setAppliedTeam(teamsFromParams);
            }
        }

        const isDefaultPlayersTab =
            activeTab === 'players'
            && (!spv.tab || spv.tab === 'players')
            && !spv.q
            && !spv.active
            && !spv.ob
            && !spv.pos
            && !spv.team
            && !spv.page;

        if (isDefaultPlayersTab) {
            setManualSearchTrigger(0);
            setPlayerResults([]);
            setPlayerTotal(0);
            setPlayerSearching(false);
            setSuppressInlineSearchStatus(false);
            setShowAdvancedFilters(false);
            setShowCoreAdvancedFilters(false);
            setShowPitcherCareerFilters(false);
            setShowBattingCareerFilters(false);
            setShowDefenseCareerFilters(false);
            setIsPaging(false);
            lastSearchSignatureRef.current = '';
            lastSearchPageRef.current = 1;
            clearAdvancedFilters();
        }
    }, [spv.tab, spv.q, spv.active, spv.ob, spv.pos, spv.team, spv.page, activeTab]);


    // 検索クエリが変わったらデバウンス後にURLを更新
    useEffect(() => {
        if (activeTab !== 'players') return;

        // IME変換中は更新しない
        if (isComposing) return;

        const timer = window.setTimeout(() => {
            const newParams = new URLSearchParams(window.location.search);
            const originalSearch = newParams.toString();

            // playerQueryが空だがURLにすでにqがある場合は外部ナビゲーション中なのでスキップ
            // （ヘッダー検索でresetPlayerFiltersによりplayerQueryが''になるが、
            //   router.pushで設定したq値をreplaceで消さないようにするため）
            const currentQInUrl = newParams.get('q') || '';
            if (!playerQuery.trim() && currentQInUrl) {
                return;
            }

            if (isPathPlayersRoute) {
                newParams.delete('tab');
            } else {
                newParams.set('tab', 'players');
            }

            if (playerQuery.trim()) {
                newParams.set('q', playerQuery);
            } else {
                newParams.delete('q');
            }

            if (rosterStatus === 'ob') {
                newParams.set('active', '0');
                newParams.set('ob', '1');
            } else if (rosterStatus === 'active') {
                newParams.set('active', '1');
                newParams.delete('ob');
            } else {
                newParams.delete('active');
                newParams.delete('ob');
            }

            if (selectedPosition === POS_ALL) {
                newParams.set('pos', POS_ALL);
            } else if (selectedPosition === DEFAULT_SEARCH_POSITION) {
                newParams.delete('pos');
            } else {
                newParams.set('pos', selectedPosition);
            }

            if (playerPage > 1) {
                newParams.set('page', String(playerPage));
            } else {
                newParams.delete('page');
            }

            if (appliedTeam.length > 0) {
                newParams.set('team', appliedTeam.join(','));
            } else {
                newParams.delete('team');
            }

            if (newParams.toString() !== originalSearch) {
                router.replace((pathname ?? '') + '?' + newParams.toString());
            }
        }, 300);

        return () => window.clearTimeout(timer);
    }, [playerQuery, isComposing, activeTab, isPathPlayersRoute, rosterStatus, selectedPosition, playerPage, appliedTeam, pathname, router]);

    useEffect(() => {
        if (activeTab !== 'players') {
            setIsPaging(false);
            return;
        }

        // IME変換中は検索しない
        if (isComposing) {
            setIsPaging(false);
            return;
        }

        const q = playerQuery.trim();
        const isManualSearch = manualSearchTrigger !== lastManualSearchTriggerRef.current;
        lastManualSearchTriggerRef.current = manualSearchTrigger;
        const hasAppliedDefenseFilterContext = appliedDefenseSeasonYear !== null
            || Object.keys(appliedDefenseCareerFilters).length > 0
            || Object.keys(appliedDefenseSeasonFilters).length > 0;
        const normalizedAppliedDefenseSearchMode: DefenseSearchMode = hasAppliedDefenseFilterContext
            ? appliedDefenseSearchMode
            : 'total';
        const normalizedAppliedDefensePosition = normalizedAppliedDefenseSearchMode === 'position'
            ? appliedDefensePosition
            : null;
        const searchSignature = `${activeTab}|${q}|${rosterStatus}|${selectedPosition}|${appliedPitch}|${appliedBat}|${appliedNationality.join(',')}|${appliedFirstYear}|${appliedLastYear}|${appliedTeam.join(',')}|${serializePitcherCareerAppliedFilters(appliedPitcherCareerFilters)}|${serializeBattingCareerAppliedFilters(appliedBattingCareerFilters)}|${serializeDefenseCareerAppliedFilters(appliedDefenseCareerFilters)}|${appliedPitcherSeasonYear}|${serializePitcherCareerAppliedFilters(appliedPitcherSeasonFilters)}|${appliedBattingSeasonYear}|${serializeBattingCareerAppliedFilters(appliedBattingSeasonFilters)}|${appliedDefenseSeasonYear}|${serializeDefenseCareerAppliedFilters(appliedDefenseSeasonFilters)}|${normalizedAppliedDefenseSearchMode}|${normalizedAppliedDefensePosition ?? ''}|${manualSearchTrigger}`;
        const isPagingRequest = !isManualSearch
            && lastSearchSignatureRef.current === searchSignature
            && lastSearchPageRef.current !== playerPage;
        lastSearchSignatureRef.current = searchSignature;
        lastSearchPageRef.current = playerPage;

        if (!q && manualSearchTrigger === 0) {
            setPlayerResults([]);
            setPlayerTotal(0);
            setPlayerSearching(false);
            setIsPaging(false);
            return;
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        setIsPaging(isPagingRequest);
        setPlayerSearching(true);
        const timer = window.setTimeout(() => {
            const searchFilters: PlayerSearchFilters = {
                active: rosterStatus === 'ob' ? 0 : 1,
                ob: rosterStatus === 'active' ? 0 : 1,
                pos: selectedPosition === POS_ALL ? POS_ALL : selectedPosition,
                team: appliedTeam.length > 0 ? appliedTeam.join(',') : undefined,
                pitch: appliedPitch !== 'all' ? appliedPitch : undefined,
                bat: appliedBat !== 'all' ? appliedBat : undefined,
                nationality: appliedNationality.length > 0 ? appliedNationality.join(',') : undefined,
                first_year: appliedFirstYear ?? undefined,
                last_year: appliedLastYear ?? undefined,
                pitcher_career: Object.keys(appliedPitcherCareerFilters).length > 0 ? appliedPitcherCareerFilters : undefined,
                batter_career: Object.keys(appliedBattingCareerFilters).length > 0 ? appliedBattingCareerFilters : undefined,
                defense_career: Object.keys(appliedDefenseCareerFilters).length > 0 ? appliedDefenseCareerFilters : undefined,
                pitcher_season_year: appliedPitcherSeasonYear ?? undefined,
                pitcher_season: appliedPitcherSeasonYear !== null && Object.keys(appliedPitcherSeasonFilters).length > 0 ? appliedPitcherSeasonFilters : undefined,
                batter_season_year: appliedBattingSeasonYear ?? undefined,
                batter_season: appliedBattingSeasonYear !== null && Object.keys(appliedBattingSeasonFilters).length > 0 ? appliedBattingSeasonFilters : undefined,
                defense_season_year: appliedDefenseSeasonYear ?? undefined,
                defense_season: appliedDefenseSeasonYear !== null && Object.keys(appliedDefenseSeasonFilters).length > 0 ? appliedDefenseSeasonFilters : undefined,
                defense_scope: hasAppliedDefenseFilterContext ? normalizedAppliedDefenseSearchMode : undefined,
                defense_position: hasAppliedDefenseFilterContext && normalizedAppliedDefenseSearchMode === 'position'
                    ? (normalizedAppliedDefensePosition ?? undefined)
                    : undefined,
            };
            fetchPlayerSearch(q, signal, searchFilters, playerPage).then((data) => {
                if (!signal.aborted) {
                    const nextTotalPages = typeof data.totalPages === 'number'
                        ? data.totalPages
                        : (data.total > 0 ? Math.ceil(data.total / PLAYER_SEARCH_PAGE_SIZE) : 0);
                    if (nextTotalPages > 0 && playerPage > nextTotalPages) {
                        setPlayerPage(nextTotalPages);
                        return;
                    }
                    if (nextTotalPages === 0 && playerPage > 1) {
                        setPlayerPage(1);
                        return;
                    }
                    setPlayerResults(data.results || []);
                    setPlayerTotal(data.total || 0);
                    setPlayerSearching(false);
                    setIsPaging(false);
                }
            }).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error('Error searching players:', error);
                    setPlayerSearching(false);
                    setIsPaging(false);
                }
            });
        }, 250);

        return () => {
            window.clearTimeout(timer);
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, playerQuery, isComposing, rosterStatus, selectedPosition, playerPage, appliedTeam, manualSearchTrigger]);

    const handleRowClick = (e: React.MouseEvent, path: string) => {
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
            return;
        }
        if ((e.target as HTMLElement).closest('a')) {
            return;
        }
        router.push(path);
    };

    const handleTeamCardClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.defaultPrevented) return;
        if (e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        setIsTeamRoutePending(true);
    };

    useEffect(() => {
        if (!isTeamRoutePending) return;
        const timer = window.setTimeout(() => setIsTeamRoutePending(false), 3000);
        return () => window.clearTimeout(timer);
    }, [isTeamRoutePending]);

    if (loading) return <div className="loading">Loading...</div>;

    const CENTRAL_2025_ORDER = [
        'hanshin_tigers',
        'yokohama_dena_baystars',
        'yomiuri_giants',
        'chunichi_dragons',
        'hiroshima_toyo_carp',
        'tokyo_yakult_swallows',
    ] as const;

    const PACIFIC_2025_ORDER = [
        'fukuoka_softbank_hawks',
        'hokkaido_nipponham_fighters',
        'orix_buffaloes',
        'tohoku_rakuten_golden_eagles',
        'saitama_seibu_lions',
        'chiba_lotte_marines',
    ] as const;

    const teamsBySlug = new Map<string, Team>(teams.map(t => [t.slug, t]));
    const orderedCentralTeams = CENTRAL_2025_ORDER.map(slug => teamsBySlug.get(slug)).filter(Boolean) as Team[];
    const orderedPacificTeams = PACIFIC_2025_ORDER.map(slug => teamsBySlug.get(slug)).filter(Boolean) as Team[];

    // 検索クエリ変更ハンドラー
    const handleSearchChange = (query: string) => {
        // ローカルステートを即座に更新（入力がスムーズになる）
        setPlayerQuery(query);
        setPlayerPage(1);
        setSuppressInlineSearchStatus(false);
    };

    const handleRosterStatusChange = (value: RosterStatus) => {
        setRosterStatus(value);
        setPlayerPage(1);
        setSuppressInlineSearchStatus(false);
    };

    const handlePositionChange = (value: string) => {
        setSelectedPosition(value);
        setPlayerPage(1);
        setSuppressInlineSearchStatus(false);
    };

    const handlePitcherCareerOpChange = (key: PitcherCareerFilterKey, op: PitcherCareerComparisonOperator) => {
        setSelectedPitcherCareerFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                op,
            },
        }));
    };

    const handlePitcherCareerValueChange = (key: PitcherCareerFilterKey, value: string) => {
        setSelectedPitcherCareerFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }));
    };

    const clearPitcherCareerFilters = () => {
        setSelectedPitcherCareerFilters(createPitcherCareerDraftFilters());
    };

    const handleBattingCareerOpChange = (key: BattingCareerFilterKey, op: PitcherCareerComparisonOperator) => {
        setSelectedBattingCareerFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                op,
            },
        }));
    };

    const handleBattingCareerValueChange = (key: BattingCareerFilterKey, value: string) => {
        setSelectedBattingCareerFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }));
    };

    const clearBattingCareerFilters = () => {
        setSelectedBattingCareerFilters(createBattingCareerDraftFilters());
    };

    const handleDefenseCareerOpChange = (key: DefenseCareerFilterKey, op: PitcherCareerComparisonOperator) => {
        setSelectedDefenseCareerFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                op,
            },
        }));
    };

    const handleDefenseCareerValueChange = (key: DefenseCareerFilterKey, value: string) => {
        setSelectedDefenseCareerFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }));
    };

    const clearDefenseCareerFilters = () => {
        setSelectedDefenseCareerFilters(createDefenseCareerDraftFilters());
    };

    const mapSearchPositionToDefensePosition = (position: string): DefensePositionKey => {
        if (position === POS_C) return 'C';
        if (position === POS_1B) return '1B';
        if (position === POS_2B) return '2B';
        if (position === POS_3B) return '3B';
        if (position === POS_SS) return 'SS';
        if (position === POS_OF) return 'OF';
        if (position === POS_IF) return 'IF';
        return 'P';
    };

    const handleDefenseSearchModeChange = (mode: DefenseSearchMode) => {
        setSelectedDefenseSearchMode(mode);
        if (mode === 'total') {
            setSelectedDefensePosition(null);
        } else if (selectedDefensePosition === null) {
            setSelectedDefensePosition(mapSearchPositionToDefensePosition(selectedPosition));
        }
    };

    const handlePitcherSeasonOpChange = (key: PitcherCareerFilterKey, op: PitcherCareerComparisonOperator) => {
        setSelectedPitcherSeasonFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                op,
            },
        }));
    };

    const handlePitcherSeasonValueChange = (key: PitcherCareerFilterKey, value: string) => {
        setSelectedPitcherSeasonFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }));
    };

    const clearPitcherSeasonFilters = () => {
        setSelectedPitcherSeasonYear('career');
        setSelectedPitcherSeasonFilters(createPitcherCareerDraftFilters());
    };

    const handleBattingSeasonOpChange = (key: BattingCareerFilterKey, op: PitcherCareerComparisonOperator) => {
        setSelectedBattingSeasonFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                op,
            },
        }));
    };

    const handleBattingSeasonValueChange = (key: BattingCareerFilterKey, value: string) => {
        setSelectedBattingSeasonFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }));
    };

    const clearBattingSeasonFilters = () => {
        setSelectedBattingSeasonYear('career');
        setSelectedBattingSeasonFilters(createBattingCareerDraftFilters());
    };

    const handleDefenseSeasonOpChange = (key: DefenseCareerFilterKey, op: PitcherCareerComparisonOperator) => {
        setSelectedDefenseSeasonFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                op,
            },
        }));
    };

    const handleDefenseSeasonValueChange = (key: DefenseCareerFilterKey, value: string) => {
        setSelectedDefenseSeasonFilters(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }));
    };

    const clearDefenseSeasonFilters = () => {
        setSelectedDefenseSeasonYear('career');
        setSelectedDefenseSeasonFilters(createDefenseCareerDraftFilters());
    };

    const clearProfileFilters = () => {
        setSelectedTeam([]);
        setSelectedNationality([]);
        setSelectedPitch('all');
        setSelectedBat('all');
        setSelectedFirstYear(null);
        setSelectedLastYear(null);
    };

    const isSeasonYearSelected = (value: CareerYearSelection): value is number => typeof value === 'number';
    const isCareerYearSelected = (value: CareerYearSelection) => value === 'career';

    const selectedPitcherUnifiedDraftFilters = isCareerYearSelected(selectedPitcherSeasonYear)
        ? selectedPitcherCareerFilters
        : selectedPitcherSeasonFilters;
    const selectedBattingUnifiedDraftFilters = isCareerYearSelected(selectedBattingSeasonYear)
        ? selectedBattingCareerFilters
        : selectedBattingSeasonFilters;
    const selectedDefenseUnifiedDraftFilters = isCareerYearSelected(selectedDefenseSeasonYear)
        ? selectedDefenseCareerFilters
        : selectedDefenseSeasonFilters;

    const selectedPitcherUnifiedFilterCount = (isSeasonYearSelected(selectedPitcherSeasonYear) ? 1 : 0) + PITCHER_CAREER_FILTER_DEFS.reduce((count, stat) => {
        return count + (selectedPitcherUnifiedDraftFilters[stat.key].value.trim() ? 1 : 0);
    }, 0);
    const selectedBattingUnifiedFilterCount = (isSeasonYearSelected(selectedBattingSeasonYear) ? 1 : 0) + BATTING_CAREER_FILTER_DEFS.reduce((count, stat) => {
        return count + (selectedBattingUnifiedDraftFilters[stat.key].value.trim() ? 1 : 0);
    }, 0);
    const npbTeamSlugs = new Set<string>([...CENTRAL_2025_ORDER, ...PACIFIC_2025_ORDER]);
    const mlbTeamSlugs = new Set<string>(mlbTeams.map(mt => mt.teamID));
    const selectedNpbTeamFilterCount = selectedTeam.some(slug => npbTeamSlugs.has(slug)) ? 1 : 0;
    const selectedMlbTeamFilterCount = selectedTeam.some(slug => mlbTeamSlugs.has(slug)) ? 1 : 0;
    const selectedProfileFilterCount = selectedNpbTeamFilterCount
        + selectedMlbTeamFilterCount
        + (selectedNationality.length > 0 ? 1 : 0)
        + (selectedPitch !== 'all' ? 1 : 0)
        + (selectedBat !== 'all' ? 1 : 0)
        + (selectedFirstYear !== null ? 1 : 0)
        + (selectedLastYear !== null ? 1 : 0);
    const selectedDefenseScopeFilterCount = selectedDefenseSearchMode === 'position'
        ? 1 + (selectedDefensePosition ? 1 : 0)
        : 0;
    const selectedDefenseUnifiedFilterCount = (isSeasonYearSelected(selectedDefenseSeasonYear) ? 1 : 0) + selectedDefenseScopeFilterCount + DEFENSE_CAREER_FILTER_DEFS.reduce((count, stat) => {
        return count + (selectedDefenseUnifiedDraftFilters[stat.key].value.trim() ? 1 : 0);
    }, 0);

    const yearAnyLabel = t('指定なし', 'Any', 'Cualquiera');
    const yearCareerLabel = t('通算', 'Career', 'Carrera');
    const yearSearchPlaceholder = t('年で検索...', 'Search year...', 'Buscar año...');
    const yearNoResultLabel = t('一致する年がありません', 'No matching years', 'No hay años coincidentes');
    const mobileYearLabel = showYearModal === 'first'
        ? t('初出場年', 'First Year', 'Primer Año')
        : showYearModal === 'last'
            ? t('最終出場年', 'Last Year', 'Último Año')
            : showYearModal === 'pitcher_season'
                ? t('投手年度', 'Pitcher Season', 'Temporada de lanzador')
                : showYearModal === 'batter_season'
                    ? t('打撃年度', 'Batting Season', 'Temporada de bateo')
                    : t('守備年度', 'Defense Season', 'Temporada defensiva');
    const mobileYearValue = showYearModal === 'first'
        ? selectedFirstYear
        : showYearModal === 'last'
            ? selectedLastYear
            : showYearModal === 'pitcher_season'
                ? selectedPitcherSeasonYear
                : showYearModal === 'batter_season'
                    ? selectedBattingSeasonYear
                    : showYearModal === 'defense_season'
                        ? selectedDefenseSeasonYear
            : null;
    const mobileYearSupportsCareer = showYearModal === 'pitcher_season'
        || showYearModal === 'batter_season'
        || showYearModal === 'defense_season';
    const mobileYearOptions = filterYearOptions(YEAR_FILTER_OPTIONS, mobileYearSearch);

    const handleMobileYearSelect = (year: CareerYearSelection) => {
        if (showYearModal === 'first') {
            if (typeof year === 'number' || year === null) {
                setSelectedFirstYear(year);
            }
        } else if (showYearModal === 'last') {
            if (typeof year === 'number' || year === null) {
                setSelectedLastYear(year);
            }
        } else if (showYearModal === 'pitcher_season') {
            setSelectedPitcherSeasonYear(year);
        } else if (showYearModal === 'batter_season') {
            setSelectedBattingSeasonYear(year);
        } else if (showYearModal === 'defense_season') {
            setSelectedDefenseSeasonYear(year);
        }
        setShowYearModal(null);
    };

    const totalPages = playerTotal > 0 ? Math.ceil(playerTotal / PLAYER_SEARCH_PAGE_SIZE) : 0;
    const pageStartIndex = playerTotal > 0 ? ((playerPage - 1) * PLAYER_SEARCH_PAGE_SIZE) + 1 : 0;
    const pageEndIndex = playerTotal > 0 ? Math.min(playerPage * PLAYER_SEARCH_PAGE_SIZE, playerTotal) : 0;
    const getPaginationItems = (): Array<number | 'ellipsis'> => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const items: Array<number | 'ellipsis'> = [1];
        const start = Math.max(2, playerPage - 1);
        const end = Math.min(totalPages - 1, playerPage + 1);

        if (start > 2) items.push('ellipsis');
        for (let p = start; p <= end; p += 1) items.push(p);
        if (end < totalPages - 1) items.push('ellipsis');

        items.push(totalPages);
        return items;
    };
    const renderPagination = (position: 'top' | 'bottom') => {
        if (totalPages <= 1) return null;
        const paginationItems = getPaginationItems();
        return (
            <div className={`flex items-center justify-center gap-3 ${position === 'bottom' ? 'mt-3' : 'mt-0'} ${position === 'top' ? 'mb-2' : 'mb-0'}`}>
                <button
                    onClick={() => setPlayerPage(prev => Math.max(1, prev - 1))}
                    disabled={playerSearching || playerPage <= 1}
                    className={`rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] px-[10px] py-1 text-[12px] text-[var(--color-text-primary)] ${(playerSearching || playerPage <= 1) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                    {t('前へ', 'Prev', 'Anterior')}
                </button>
                <div className="flex items-center gap-1">
                    {paginationItems.map((item, idx) => (
                        item === 'ellipsis' ? (
                            <span key={`ellipsis-${idx}`} className="px-1 text-[12px] text-[var(--color-text-subtle)]">...</span>
                        ) : (
                            <button
                                key={item}
                                onClick={() => setPlayerPage(item)}
                                disabled={playerSearching || item === playerPage}
                                className={`min-w-[28px] rounded-[4px] border border-[var(--color-border-default)] px-2 py-1 text-[12px] ${(playerSearching || item === playerPage) ? 'cursor-not-allowed' : 'cursor-pointer'} ${item === playerPage ? 'bg-[var(--color-text-primary)] text-[var(--color-surface-card)]' : 'bg-[var(--color-surface-card)] text-[var(--color-text-primary)]'} ${playerSearching ? 'opacity-50' : 'opacity-100'}`}
                            >
                                {item}
                            </button>
                        )
                    ))}
                </div>
                <button
                    onClick={() => setPlayerPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={playerSearching || playerPage >= totalPages}
                    className={`rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] px-[10px] py-1 text-[12px] text-[var(--color-text-primary)] ${(playerSearching || playerPage >= totalPages) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                    {t('次へ', 'Next', 'Siguiente')}
                </button>
            </div>
        );
    };

    return (
        <div className={`home-page${isTeamRoutePending ? ' is-team-route-pending' : ''}`}>
            <h1 className="sr-only">{APP_NAME}</h1>

            <nav className="awards-breadcrumb">
                <Link href={toLocalePath('/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                {' › '}
                <span>
                    {activeTab === 'npb'
                        ? t('プロ野球（NPB）チーム一覧', 'Nippon Professional Baseball (NPB) Teams', 'Equipos de Béisbol Profesional Japonés (NPB)')
                        : activeTab === 'mlb'
                            ? t('メジャーリーグ（MLB）チーム一覧', 'Major League Baseball (MLB) Teams', 'Equipos de Grandes Ligas (MLB)')
                            : t('選手検索', 'Player Search', 'Búsqueda de jugadores')}
                </span>
            </nav>

            {(activeTab === 'npb' || activeTab === 'mlb') && isTeamRoutePending && (
                <div className="team-route-loading" role="status" aria-live="polite">
                    {t('読み込み中...', 'Loading...', 'Cargando...')}
                </div>
            )}

            {activeTab === 'npb' && (
                <div className="league-container">
                    <section className="league-section">
                        <h2 className="league-header">{t('セ・リーグ', 'Central League', 'Liga Central')}</h2>
                        <div className="team-grid npb-team-grid">
                            {orderedCentralTeams.map(team => {
                                const colors = TEAM_COLORS[team.slug] || { main: team.color, sub: team.color };
                                return (
                                    <Link
                                        href={toLocalePath(`/npb/${team.slug}`)}
                                        key={`npb-central-${team.slug}`}
                                        onClick={handleTeamCardClick}
                                        className="team-card team-card-themed"
                                        style={{ '--team-main': colors.main, '--team-sub': colors.sub } as React.CSSProperties}
                                    >
                                        <h2 className="team-name">{getTeamName(team.slug, team.official_name)}</h2>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>

                    <section className="league-section">
                        <h2 className="league-header">{t('パ・リーグ', 'Pacific League', 'Liga del Pacífico')}</h2>
                        <div className="team-grid npb-team-grid">
                            {orderedPacificTeams.map(team => {
                                const colors = TEAM_COLORS[team.slug] || { main: team.color, sub: team.color };
                                return (
                                    <Link
                                        href={toLocalePath(`/npb/${team.slug}`)}
                                        key={`npb-pacific-${team.slug}`}
                                        onClick={handleTeamCardClick}
                                        className="team-card team-card-themed"
                                        style={{ '--team-main': colors.main, '--team-sub': colors.sub } as React.CSSProperties}
                                    >
                                        <h2 className="team-name">{getTeamName(team.slug, team.official_name)}</h2>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>

                </div>
            )}

            {activeTab === 'mlb' && (
                <div className="league-container">
                    <section className="league-section">
                        <h2 className="league-header">{t('アメリカン リーグ', 'American League', 'Liga Americana')}</h2>
                        {['E', 'C', 'W'].map(div => {
                            const divisionTeams = mlbTeams.filter(t => t.lgID === 'AL' && t.divID === div);
                            if (divisionTeams.length === 0) return null;
                            return (
                                <div key={div} className="division-block">
                                    <h3 className="division-header">
                                        {div === 'E' ? t('東地区', 'East', 'Este') : div === 'C' ? t('中地区', 'Central', 'Central') : t('西地区', 'West', 'Oeste')}
                                    </h3>
                                    <div className="team-grid">
                                        {divisionTeams.map(team => {
                                            const colors = TEAM_COLORS[team.teamID] || { main: '#ccc', sub: '#ccc' };
                                            return (
                                                <Link
                                                    href={toLocalePath(`/mlb/${team.teamID}`)}
                                                    key={team.teamID}
                                                    onClick={handleTeamCardClick}
                                                    className="team-card team-card-themed"
                                                    style={{ '--team-main': colors.main, '--team-sub': colors.sub } as React.CSSProperties}
                                                >
                                                    <h2 className="team-name">{getTeamName(team.teamID, team.name)}</h2>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </section>

                    <section className="league-section">
                        <h2 className="league-header">{t('ナショナル リーグ', 'National League', 'Liga Nacional')}</h2>
                        {['E', 'C', 'W'].map(div => {
                            const divisionTeams = mlbTeams.filter(t => t.lgID === 'NL' && t.divID === div);
                            if (divisionTeams.length === 0) return null;
                            return (
                                <div key={div} className="division-block">
                                    <h3 className="division-header">
                                        {div === 'E' ? t('東地区', 'East', 'Este') : div === 'C' ? t('中地区', 'Central', 'Central') : t('西地区', 'West', 'Oeste')}
                                    </h3>
                                    <div className="team-grid">
                                        {divisionTeams.map(team => {
                                            const colors = TEAM_COLORS[team.teamID] || { main: '#ccc', sub: '#ccc' };
                                            return (
                                                <Link
                                                    href={toLocalePath(`/mlb/${team.teamID}`)}
                                                    key={team.teamID}
                                                    onClick={handleTeamCardClick}
                                                    className="team-card team-card-themed"
                                                    style={{ '--team-main': colors.main, '--team-sub': colors.sub } as React.CSSProperties}
                                                >
                                                    <h2 className="team-name">{getTeamName(team.teamID, team.name)}</h2>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </section>

                </div>
            )}

            {activeTab === 'players' && (
                <div className={`player-search${playerSearching ? ' is-searching' : ''}`}>
                    <div className="filter-controls mb-[15px] rounded-[8px] bg-[var(--color-surface-muted)] px-5 py-[15px] text-[13px]">
                        <div className="status-radios desktop-filter-row mb-3 flex flex-wrap items-center">
                            <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('対象', 'Roster', 'Plantilla')}</span>
                            <label className="mr-5 flex cursor-pointer items-center">
                                <input
                                    type="radio"
                                    name="player-roster"
                                    value="all"
                                    checked={rosterStatus === 'all'}
                                    onChange={(e) => handleRosterStatusChange(e.target.value as RosterStatus)}
                                    className="mr-1"
                                /> {t('すべて', 'All', 'Todos')}
                            </label>
                            <label className="mr-5 flex cursor-pointer items-center">
                                <input
                                    type="radio"
                                    name="player-roster"
                                    value="active"
                                    checked={rosterStatus === 'active'}
                                    onChange={(e) => handleRosterStatusChange(e.target.value as RosterStatus)}
                                    className="mr-1"
                                /> {t('現役選手', 'Active', 'Activo')}
                            </label>
                            <label className="flex cursor-pointer items-center">
                                <input
                                    type="radio"
                                    name="player-roster"
                                    value="ob"
                                    checked={rosterStatus === 'ob'}
                                    onChange={(e) => handleRosterStatusChange(e.target.value as RosterStatus)}
                                    className="mr-1"
                                /> {t('OB選手', 'OB', 'OB')}
                            </label>
                        </div>

                        <div className="status-selects mobile-filter-row mb-3 w-full flex-wrap items-center">
                            <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('対象', 'Roster', 'Plantilla')}</span>
                            <select
                                aria-label={t('対象', 'Roster', 'Plantilla')}
                                value={rosterStatus}
                                onChange={(e) => handleRosterStatusChange(e.target.value as RosterStatus)}
                                className="min-w-0 flex-1 rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px]"
                            >
                                <option value="all">{t('すべて', 'All', 'Todos')}</option>
                                <option value="active">{t('現役選手', 'Active', 'Activo')}</option>
                                <option value="ob">{t('OB選手', 'OB', 'OB')}</option>
                            </select>
                        </div>

                        <div className="position-radios desktop-filter-row mb-3 flex flex-wrap items-center">
                            <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('守備位置', 'Position', 'Posición')}</span>
                            {[POS_ALL_LABEL, ...POSITION_OPTIONS].map(pos => (
                                <label key={pos} className="mb-1 mr-[15px] flex cursor-pointer items-center">
                                    <input
                                        type="radio"
                                        name="player-position"
                                        value={pos === POS_ALL_LABEL ? POS_ALL : pos}
                                        checked={selectedPosition === (pos === POS_ALL_LABEL ? POS_ALL : pos)}
                                        onChange={(e) => handlePositionChange(e.target.value)}
                                        className="mr-1"
                                    /> {t(pos, pos === POS_ALL_LABEL ? 'All' : getPositionShortLabel(pos))}
                                </label>
                            ))}
                        </div>

                        <div className="position-selects mobile-filter-row mb-3 w-full flex-wrap items-center">
                            <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('守備位置', 'Position', 'Posición')}</span>
                            <select
                                aria-label={t('守備位置', 'Position', 'Posición')}
                                value={selectedPosition}
                                onChange={(e) => handlePositionChange(e.target.value)}
                                className="min-w-0 flex-1 rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px]"
                            >
                                {[POS_ALL_LABEL, ...POSITION_OPTIONS].map(pos => (
                                    <option key={pos} value={pos === POS_ALL_LABEL ? POS_ALL : pos}>
                                        {t(pos, pos === POS_ALL_LABEL ? 'All' : getPositionShortLabel(pos))}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="text-filter mb-2 flex flex-wrap items-center">
                            <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('選手名', 'Name', 'Nombre')}</span>
                            <div className="flex flex-1 items-center gap-2">
                                <input
                                    className="player-search-input m-0 max-w-[300px] flex-1 rounded-[4px] text-[13px]"
                                    value={playerQuery}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    onCompositionStart={() => setIsComposing(true)}
                                    onCompositionEnd={(e) => {
                                        setIsComposing(false);
                                        handleSearchChange(e.currentTarget.value);
                                    }}
                                    placeholder={t('選手名で検索', 'Search by player name', 'Buscar por nombre')}
                                />
                                {playerSearching && !isPaging && !suppressInlineSearchStatus && <div className="player-search-status">{t('検索中...', 'Searching...', 'Buscando...')}</div>}
                            </div>
                        </div>

                        {/* 詳細条件ボタン（PC/SP共通） */}
                        <div className="ml-[100px] flex items-center">
                            <button
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                disabled={playerSearching}
                                className="flex cursor-pointer items-center gap-1 rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-2 py-1 text-[12px] text-[var(--color-text-secondary)]"
                            >
                                {showAdvancedFilters ? t('詳細条件を閉じる', 'Hide Advanced', 'Ocultar Avanzado') : t('詳細条件を開く', 'Show Advanced', 'Mostrar Avanzado')}
                                <span className={`inline-block transition-transform duration-200 ${showAdvancedFilters ? 'rotate-180' : 'rotate-0'}`}></span>
                            </button>
                        </div>

                        {/* 詳細条件パネル（PC/SP共通） */}
                        {showAdvancedFilters && (
                            <div className="advanced-filters mt-[15px] border-t border-dashed border-[var(--color-border-default)] pt-[15px]">


                                <div className="advanced-filter-accordion">
                                    <div className="pitcher-career-filter-header">
                                        <button
                                            type="button"
                                            className="pitcher-career-filter-toggle"
                                            onClick={() => setShowCoreAdvancedFilters(prev => !prev)}
                                            disabled={playerSearching}
                                            aria-expanded={showCoreAdvancedFilters}
                                        >
                                            <span className="pitcher-career-filter-title">{t('プロフィール', 'Profile', 'Perfil')}</span>
                                            <span className="pitcher-career-filter-chevron">{showCoreAdvancedFilters ? '▲' : '▼'}</span>
                                        </button>
                                        <div className="pitcher-career-filter-actions">
                                            <span className="pitcher-career-filter-count">
                                                {t('設定', 'Set', 'Aplicado')}: {selectedProfileFilterCount}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={playerSearching}
                                                onClick={clearProfileFilters}
                                                className="pitcher-career-filter-clear"
                                            >
                                                {t('解除', 'Clear', 'Borrar')}
                                            </button>
                                        </div>
                                    </div>
                                    {showCoreAdvancedFilters && (
                                        <>
                                {/* PC用：NPB/メジャーリーグ（MLB）チームリストをインライン表示 */}
                                <div className="desktop-advanced-btn block">
                                    {/* NPB */}
                                    <div className="mb-2 flex items-start">
                                        <div className="w-[100px] shrink-0 text-left">
                                            <span className="font-semibold text-[var(--color-text-secondary)]">
                                                {t('プロ野球（NPB）', 'Nippon Professional Baseball (NPB)', 'Béisbol Profesional Japonés (NPB)')}
                                            </span>
                                            <div className="mt-1 flex gap-1">
                                                <button disabled={playerSearching} onClick={() => { const npbSlugs: string[] = [...CENTRAL_2025_ORDER, ...PACIFIC_2025_ORDER]; setSelectedTeam(prev => [...new Set([...prev, ...npbSlugs])]); }} className="cursor-pointer rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-[6px] py-[1px] text-[10px] text-[#888]">{t('全選択', 'All', 'Todos')}</button>
                                                <button disabled={playerSearching} onClick={() => { const npbSlugs: string[] = [...CENTRAL_2025_ORDER, ...PACIFIC_2025_ORDER]; setSelectedTeam(prev => prev.filter(s => !npbSlugs.includes(s))); }} className="cursor-pointer rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-[6px] py-[1px] text-[10px] text-[#888]">{t('解除', 'Clear', 'Borrar')}</button>
                                            </div>
                                        </div>
                                        <div className="flex flex-1 flex-col gap-1">
                                            <div className="flex flex-wrap items-center gap-[6px]">
                                                <span className="whitespace-nowrap text-[11px] font-semibold text-[#888]">{t('セリーグ', 'Central', 'Liga Central')}</span>
                                                {CENTRAL_2025_ORDER.map(slug => (
                                                    <label key={slug} className="flex cursor-pointer items-center whitespace-nowrap text-[12px]">
                                                        <input type="checkbox" checked={selectedTeam.includes(slug)} onChange={(e) => { if (e.target.checked) { setSelectedTeam(prev => [...prev, slug]); } else { setSelectedTeam(prev => prev.filter(s => s !== slug)); } }} className="mr-[3px]" />
                                                        {getTeamName(slug, slug)}
                                                    </label>
                                                ))}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-[6px]">
                                                <span className="whitespace-nowrap text-[11px] font-semibold text-[#888]">{t('パリーグ', 'Pacific', 'Liga del Pacífico')}</span>
                                                {PACIFIC_2025_ORDER.map(slug => (
                                                    <label key={slug} className="flex cursor-pointer items-center whitespace-nowrap text-[12px]">
                                                        <input type="checkbox" checked={selectedTeam.includes(slug)} onChange={(e) => { if (e.target.checked) { setSelectedTeam(prev => [...prev, slug]); } else { setSelectedTeam(prev => prev.filter(s => s !== slug)); } }} className="mr-[3px]" />
                                                        {getTeamName(slug, slug)}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* MLB */}
                                    <div className="flex items-start">
                                        <div className="w-[100px] shrink-0 text-left">
                                            <span className="font-semibold text-[var(--color-text-secondary)]">
                                                {t('メジャーリーグ（MLB）', 'Major League Baseball (MLB)', 'Grandes Ligas (MLB)')}
                                            </span>
                                            <div className="mt-1 flex gap-1">
                                                <button disabled={playerSearching} onClick={() => { const mlbSlugs = mlbTeams.map(mt => mt.teamID); setSelectedTeam(prev => [...new Set([...prev, ...mlbSlugs])]); }} className="cursor-pointer rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-[6px] py-[1px] text-[10px] text-[#888]">{t('全選択', 'All', 'Todos')}</button>
                                                <button disabled={playerSearching} onClick={() => { const mlbSlugs = mlbTeams.map(mt => mt.teamID); setSelectedTeam(prev => prev.filter(s => !mlbSlugs.includes(s))); }} className="cursor-pointer rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-[6px] py-[1px] text-[10px] text-[#888]">{t('解除', 'Clear', 'Borrar')}</button>
                                            </div>
                                        </div>
                                        <div className="flex flex-1 flex-col gap-1">
                                            {(['AL', 'NL'] as const).map(lg =>
                                                (['E', 'C', 'W'] as const).map(div => {
                                                    const divTeams = mlbTeams.filter(mt => mt.lgID === lg && mt.divID === div);
                                                    if (divTeams.length === 0) return null;
                                                    const lgLabel = lg === 'AL' ? t('アメリカン リーグ', 'AL', 'LA') : t('ナショナル リーグ', 'NL', 'LN');
                                                    const divLabel = div === 'E' ? t('東地区', 'East', 'Este') : div === 'C' ? t('中地区', 'Central', 'Central') : t('西地区', 'West', 'Oeste');
                                                    return (
                                                        <div key={`${lg}-${div}`} className="flex flex-wrap items-center gap-[6px]">
                                                            <span className="whitespace-nowrap text-[11px] font-semibold text-[#888]">{lgLabel} {divLabel}</span>
                                                            {divTeams.map(mt => (
                                                                <label key={mt.teamID} className="flex cursor-pointer items-center whitespace-nowrap text-[12px]">
                                                                    <input type="checkbox" checked={selectedTeam.includes(mt.teamID)} onChange={(e) => { if (e.target.checked) { setSelectedTeam(prev => [...prev, mt.teamID]); } else { setSelectedTeam(prev => prev.filter(s => s !== mt.teamID)); } }} className="mr-[3px]" />
                                                                    {getTeamName(mt.teamID, mt.name)}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 国籍（PC用チェックボックス） */}
                                <div className="mt-3 flex items-start">
                                    <div className="w-[100px] shrink-0 text-left">
                                        <span className="font-semibold text-[var(--color-text-secondary)]">{t('国籍', 'Nationality', 'Nacionalidad')}</span>
                                        <div className="mt-1 flex gap-1">
                                            <button disabled={playerSearching} onClick={() => setSelectedNationality(NATIONALITY_OPTIONS.map(o => o.value))} className="cursor-pointer rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-[6px] py-[1px] text-[10px] text-[#888]">{t('全選択', 'All', 'Todos')}</button>
                                            <button disabled={playerSearching} onClick={() => setSelectedNationality([])} className="cursor-pointer rounded-[4px] border border-[var(--color-border-default)] bg-transparent px-[6px] py-[1px] text-[10px] text-[#888]">{t('解除', 'Clear', 'Borrar')}</button>
                                        </div>
                                    </div>
                                    <div className="flex flex-1 flex-wrap items-center gap-[6px]">
                                        {NATIONALITY_OPTIONS.map(opt => (
                                            <label key={opt.value} className="flex cursor-pointer items-center whitespace-nowrap text-[12px]">
                                                <input type="checkbox" checked={selectedNationality.includes(opt.value)} onChange={(e) => { if (e.target.checked) { setSelectedNationality(prev => [...prev, opt.value]); } else { setSelectedNationality(prev => prev.filter(s => s !== opt.value)); } }} className="mr-[3px]" />
                                                {t(opt.ja, opt.en, opt.es)}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* スマホ用：NPB/MLB チーム選択ボタン（モーダルを開く） */}
                                <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">
                                        {t('プロ野球（NPB）', 'Nippon Professional Baseball (NPB)', 'Béisbol Profesional Japonés (NPB)')}
                                    </span>
                                    <button
                                        onClick={() => setShowNpbTeamModal(true)}
                                        disabled={playerSearching}
                                        className="min-w-0 flex-1 cursor-pointer items-center justify-center gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                    >
                                        {t('チーム選択', 'Select Team', 'Seleccionar Equipo')}
                                        {appliedTeam.filter(s => CENTRAL_2025_ORDER.includes(s as any) || PACIFIC_2025_ORDER.includes(s as any)).length > 0 && <span className="rounded-[10px] bg-[var(--color-text-primary)] px-[6px] text-[11px] text-[var(--color-surface-card)]">{appliedTeam.filter(s => CENTRAL_2025_ORDER.includes(s as any) || PACIFIC_2025_ORDER.includes(s as any)).length}</span>}
                                    </button>
                                </div>
                                <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">
                                        {t('メジャーリーグ（MLB）', 'Major League Baseball (MLB)', 'Grandes Ligas (MLB)')}
                                    </span>
                                    <button
                                        onClick={() => setShowMlbTeamModal(true)}
                                        disabled={playerSearching}
                                        className="min-w-0 flex-1 cursor-pointer items-center justify-center gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                    >
                                        {t('チーム選択', 'Select Team', 'Seleccionar Equipo')}
                                        {appliedTeam.filter(s => mlbTeams.some(mt => mt.teamID === s)).length > 0 && <span className="rounded-[10px] bg-[var(--color-text-primary)] px-[6px] text-[11px] text-[var(--color-surface-card)]">{appliedTeam.filter(s => mlbTeams.some(mt => mt.teamID === s)).length}</span>}
                                    </button>
                                </div>

                                {/* スマホ用：国籍選択ボタン（モーダルを開く） */}
                                <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('国籍', 'Nationality', 'Nacionalidad')}</span>
                                    <button
                                        onClick={() => setShowNationalityModal(true)}
                                        disabled={playerSearching}
                                        className="min-w-0 flex-1 cursor-pointer items-center justify-center gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                    >
                                        {t('国籍選択', 'Select Nationality', 'Seleccionar Nacionalidad')}
                                        {selectedNationality.length > 0 && <span className="rounded-[10px] bg-[var(--color-text-primary)] px-[6px] text-[11px] text-[var(--color-surface-card)]">{selectedNationality.length}</span>}
                                    </button>
                                </div>

                                {/* 投打フィルター */}
                                <div className="mb-2 mt-3 flex flex-wrap items-center">
                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('投', 'Pitch', 'Lanzamiento')}</span>
                                    <div className="flex flex-1 flex-wrap items-center">
                                        {[
                                            { value: 'all', label: t('指定なし', 'All', 'Todos') },
                                            { value: '右', label: t('右投', 'Right', 'Derecho') },
                                            { value: '左', label: t('左投', 'Left', 'Izquierdo') },
                                            { value: '両', label: t('両投', 'Both', 'Ambos') },
                                        ].map(opt => (
                                            <label key={opt.value} className="mb-1 mr-[15px] flex cursor-pointer items-center">
                                                <input
                                                    type="radio"
                                                    name="player-pitch"
                                                    value={opt.value}
                                                    checked={selectedPitch === opt.value}
                                                    onChange={(e) => { setSelectedPitch(e.target.value); }}
                                                    className="mr-1"
                                                /> {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-2 flex flex-wrap items-center">
                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('打', 'Bat', 'Bateo')}</span>
                                    <div className="flex flex-1 flex-wrap items-center">
                                        {[
                                            { value: 'all', label: t('指定なし', 'All', 'Todos') },
                                            { value: '右', label: t('右打', 'Right', 'Derecho') },
                                            { value: '左', label: t('左打', 'Left', 'Izquierdo') },
                                            { value: '両', label: t('両打', 'Both', 'Ambos') },
                                        ].map(opt => (
                                            <label key={opt.value} className="mb-1 mr-[15px] flex cursor-pointer items-center">
                                                <input
                                                    type="radio"
                                                    name="player-bat"
                                                    value={opt.value}
                                                    checked={selectedBat === opt.value}
                                                    onChange={(e) => { setSelectedBat(e.target.value); }}
                                                    className="mr-1"
                                                /> {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* 年フィルター（PC: コンボボックス） */}
                                <div className="year-filter-desktop">
                                    <YearCombobox
                                        label={t('初出場年', 'First Year', 'Primer Año')}
                                        value={selectedFirstYear}
                                        onChange={setSelectedFirstYear}
                                        disabled={playerSearching}
                                        years={YEAR_FILTER_OPTIONS}
                                        anyLabel={yearAnyLabel}
                                        clearLabel={t('解除', 'Clear', 'Borrar')}
                                        searchPlaceholder={yearSearchPlaceholder}
                                        noResultLabel={yearNoResultLabel}
                                    />
                                    <YearCombobox
                                        label={t('最終出場年', 'Last Year', 'Último Año')}
                                        value={selectedLastYear}
                                        onChange={setSelectedLastYear}
                                        disabled={playerSearching}
                                        years={YEAR_FILTER_OPTIONS}
                                        anyLabel={yearAnyLabel}
                                        clearLabel={t('解除', 'Clear', 'Borrar')}
                                        searchPlaceholder={yearSearchPlaceholder}
                                        noResultLabel={yearNoResultLabel}
                                    />
                                </div>

                                {/* 年フィルター（スマホ: フルスクリーン検索） */}
                                <div className="year-filter-mobile">
                                    <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                        <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('初出場年', 'First Year', 'Primer Año')}</span>
                                        <button
                                            type="button"
                                            onClick={() => setShowYearModal('first')}
                                            disabled={playerSearching}
                                            className="min-w-0 flex-1 cursor-pointer items-center justify-between gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                        >
                                            <span>{selectedFirstYear === null ? yearAnyLabel : String(selectedFirstYear)}</span>
                                            <span className="text-[10px] text-[#999]">▼</span>
                                        </button>
                                    </div>
                                    <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                        <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('最終出場年', 'Last Year', 'Último Año')}</span>
                                        <button
                                            type="button"
                                            onClick={() => setShowYearModal('last')}
                                            disabled={playerSearching}
                                            className="min-w-0 flex-1 cursor-pointer items-center justify-between gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                        >
                                            <span>{selectedLastYear === null ? yearAnyLabel : String(selectedLastYear)}</span>
                                            <span className="text-[10px] text-[#999]">▼</span>
                                        </button>
                                    </div>
                                </div>
                                        </>
                                    )}
                                </div>

                                {/* 投手成績フィルター（通算/年度別） */}
                                <div className="pitcher-career-filter-section">
                                    <div className="pitcher-career-filter-header">
                                        <button
                                            type="button"
                                            className="pitcher-career-filter-toggle"
                                            onClick={() => setShowPitcherCareerFilters(prev => !prev)}
                                            disabled={playerSearching}
                                            aria-expanded={showPitcherCareerFilters}
                                        >
                                            <span className="pitcher-career-filter-title">{t('投手成績', 'Pitcher Stats', 'Estadísticas de lanzador')}</span>
                                            <span className="pitcher-career-filter-chevron">{showPitcherCareerFilters ? '▲' : '▼'}</span>
                                        </button>
                                        <div className="pitcher-career-filter-actions">
                                            <span className="pitcher-career-filter-count">
                                                {t('設定', 'Set', 'Aplicado')}: {selectedPitcherUnifiedFilterCount}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={playerSearching}
                                                onClick={() => {
                                                    clearPitcherCareerFilters();
                                                    clearPitcherSeasonFilters();
                                                }}
                                                className="pitcher-career-filter-clear"
                                            >
                                                {t('解除', 'Clear', 'Borrar')}
                                            </button>
                                        </div>
                                    </div>
                                    {showPitcherCareerFilters && (
                                        <>
                                            <div className="year-filter-desktop">
                                                <CareerYearCombobox
                                                    label={t('年度', 'Season', 'Temporada')}
                                                    value={selectedPitcherSeasonYear}
                                                    onChange={setSelectedPitcherSeasonYear}
                                                    disabled={playerSearching}
                                                    years={YEAR_FILTER_OPTIONS}
                                                    anyLabel={yearAnyLabel}
                                                    careerLabel={yearCareerLabel}
                                                    clearLabel={t('解除', 'Clear', 'Borrar')}
                                                    searchPlaceholder={yearSearchPlaceholder}
                                                    noResultLabel={yearNoResultLabel}
                                                />
                                            </div>
                                            <div className="year-filter-mobile">
                                                <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('年度', 'Season', 'Temporada')}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowYearModal('pitcher_season')}
                                                        disabled={playerSearching}
                                                        className="min-w-0 flex-1 cursor-pointer items-center justify-between gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                                    >
                                                        <span>{
                                                            selectedPitcherSeasonYear === null
                                                                ? yearAnyLabel
                                                                : selectedPitcherSeasonYear === 'career'
                                                                    ? yearCareerLabel
                                                                    : String(selectedPitcherSeasonYear)
                                                        }</span>
                                                        <span className="text-[10px] text-[#999]">▼</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="pitcher-career-filter-grid">
                                                {PITCHER_CAREER_FILTER_DEFS.map(stat => (
                                                    <div
                                                        key={stat.key}
                                                        className={`pitcher-career-filter-item${selectedPitcherUnifiedDraftFilters[stat.key].value.trim() ? ' is-filled' : ''}`}
                                                    >
                                                        <label className="pitcher-career-filter-label">{t(stat.ja, stat.en, stat.es)}</label>
                                                        <div className="pitcher-career-filter-controls">
                                                            <input
                                                                type="number"
                                                                inputMode="decimal"
                                                                step={stat.step}
                                                                value={selectedPitcherUnifiedDraftFilters[stat.key].value}
                                                                onChange={(event) => {
                                                                    if (isCareerYearSelected(selectedPitcherSeasonYear)) {
                                                                        handlePitcherCareerValueChange(stat.key, event.target.value);
                                                                    } else {
                                                                        handlePitcherSeasonValueChange(stat.key, event.target.value);
                                                                    }
                                                                }}
                                                                placeholder={getCareerValuePlaceholder(t, stat.key, stat.step)}
                                                                disabled={playerSearching || selectedPitcherSeasonYear === null}
                                                                className="pitcher-career-filter-value"
                                                            />
                                                            <select
                                                                value={selectedPitcherUnifiedDraftFilters[stat.key].op}
                                                                onChange={(event) => {
                                                                    const nextOp = event.target.value as PitcherCareerComparisonOperator;
                                                                    if (isCareerYearSelected(selectedPitcherSeasonYear)) {
                                                                        handlePitcherCareerOpChange(stat.key, nextOp);
                                                                    } else {
                                                                        handlePitcherSeasonOpChange(stat.key, nextOp);
                                                                    }
                                                                }}
                                                                disabled={playerSearching || selectedPitcherSeasonYear === null}
                                                                className="pitcher-career-filter-op"
                                                            >
                                                                <option value="gte">{t('以上', 'At least', 'Mínimo')}</option>
                                                                <option value="lte">{t('以下', 'At most', 'Máximo')}</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="pitcher-career-filter-section">
                                    <div className="pitcher-career-filter-header">
                                        <button
                                            type="button"
                                            className="pitcher-career-filter-toggle"
                                            onClick={() => setShowBattingCareerFilters(prev => !prev)}
                                            disabled={playerSearching}
                                            aria-expanded={showBattingCareerFilters}
                                        >
                                            <span className="pitcher-career-filter-title">{t('打撃成績', 'Batting Stats', 'Estadísticas de bateo')}</span>
                                            <span className="pitcher-career-filter-chevron">{showBattingCareerFilters ? '▲' : '▼'}</span>
                                        </button>
                                        <div className="pitcher-career-filter-actions">
                                            <span className="pitcher-career-filter-count">
                                                {t('設定', 'Set', 'Aplicado')}: {selectedBattingUnifiedFilterCount}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={playerSearching}
                                                onClick={() => {
                                                    clearBattingCareerFilters();
                                                    clearBattingSeasonFilters();
                                                }}
                                                className="pitcher-career-filter-clear"
                                            >
                                                {t('解除', 'Clear', 'Borrar')}
                                            </button>
                                        </div>
                                    </div>
                                    {showBattingCareerFilters && (
                                        <>
                                            <div className="year-filter-desktop">
                                                <CareerYearCombobox
                                                    label={t('年度', 'Season', 'Temporada')}
                                                    value={selectedBattingSeasonYear}
                                                    onChange={setSelectedBattingSeasonYear}
                                                    disabled={playerSearching}
                                                    years={YEAR_FILTER_OPTIONS}
                                                    anyLabel={yearAnyLabel}
                                                    careerLabel={yearCareerLabel}
                                                    clearLabel={t('解除', 'Clear', 'Borrar')}
                                                    searchPlaceholder={yearSearchPlaceholder}
                                                    noResultLabel={yearNoResultLabel}
                                                />
                                            </div>
                                            <div className="year-filter-mobile">
                                                <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('年度', 'Season', 'Temporada')}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowYearModal('batter_season')}
                                                        disabled={playerSearching}
                                                        className="min-w-0 flex-1 cursor-pointer items-center justify-between gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                                    >
                                                        <span>{
                                                            selectedBattingSeasonYear === null
                                                                ? yearAnyLabel
                                                                : selectedBattingSeasonYear === 'career'
                                                                    ? yearCareerLabel
                                                                    : String(selectedBattingSeasonYear)
                                                        }</span>
                                                        <span className="text-[10px] text-[#999]">▼</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="pitcher-career-filter-grid">
                                                {BATTING_CAREER_FILTER_DEFS.map(stat => (
                                                    <div
                                                        key={stat.key}
                                                        className={`pitcher-career-filter-item${selectedBattingUnifiedDraftFilters[stat.key].value.trim() ? ' is-filled' : ''}`}
                                                    >
                                                        <label className="pitcher-career-filter-label">{t(stat.ja, stat.en, stat.es)}</label>
                                                        <div className="pitcher-career-filter-controls">
                                                            <input
                                                                type="number"
                                                                inputMode="decimal"
                                                                step={stat.step}
                                                                value={selectedBattingUnifiedDraftFilters[stat.key].value}
                                                                onChange={(event) => {
                                                                    if (isCareerYearSelected(selectedBattingSeasonYear)) {
                                                                        handleBattingCareerValueChange(stat.key, event.target.value);
                                                                    } else {
                                                                        handleBattingSeasonValueChange(stat.key, event.target.value);
                                                                    }
                                                                }}
                                                                placeholder={getCareerValuePlaceholder(t, stat.key, stat.step)}
                                                                disabled={playerSearching || selectedBattingSeasonYear === null}
                                                                className="pitcher-career-filter-value"
                                                            />
                                                            <select
                                                                value={selectedBattingUnifiedDraftFilters[stat.key].op}
                                                                onChange={(event) => {
                                                                    const nextOp = event.target.value as PitcherCareerComparisonOperator;
                                                                    if (isCareerYearSelected(selectedBattingSeasonYear)) {
                                                                        handleBattingCareerOpChange(stat.key, nextOp);
                                                                    } else {
                                                                        handleBattingSeasonOpChange(stat.key, nextOp);
                                                                    }
                                                                }}
                                                                disabled={playerSearching || selectedBattingSeasonYear === null}
                                                                className="pitcher-career-filter-op"
                                                            >
                                                                <option value="gte">{t('以上', 'At least', 'Mínimo')}</option>
                                                                <option value="lte">{t('以下', 'At most', 'Máximo')}</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="pitcher-career-filter-section">
                                    <div className="pitcher-career-filter-header">
                                        <button
                                            type="button"
                                            className="pitcher-career-filter-toggle"
                                            onClick={() => setShowDefenseCareerFilters(prev => !prev)}
                                            disabled={playerSearching}
                                            aria-expanded={showDefenseCareerFilters}
                                        >
                                            <span className="pitcher-career-filter-title">{t('守備成績', 'Defense Stats', 'Estadísticas defensivas')}</span>
                                            <span className="pitcher-career-filter-chevron">{showDefenseCareerFilters ? '▲' : '▼'}</span>
                                        </button>
                                        <div className="pitcher-career-filter-actions">
                                            <span className="pitcher-career-filter-count">
                                                {t('設定', 'Set', 'Aplicado')}: {selectedDefenseUnifiedFilterCount}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={playerSearching}
                                                onClick={() => {
                                                    clearDefenseCareerFilters();
                                                    clearDefenseSeasonFilters();
                                                    setSelectedDefenseSearchMode('total');
                                                    setSelectedDefensePosition(null);
                                                }}
                                                className="pitcher-career-filter-clear"
                                            >
                                                {t('解除', 'Clear', 'Borrar')}
                                            </button>
                                        </div>
                                    </div>
                                    {showDefenseCareerFilters && (
                                        <>
                                            <div className="year-filter-desktop">
                                                <CareerYearCombobox
                                                    label={t('年度', 'Season', 'Temporada')}
                                                    value={selectedDefenseSeasonYear}
                                                    onChange={setSelectedDefenseSeasonYear}
                                                    disabled={playerSearching}
                                                    years={YEAR_FILTER_OPTIONS}
                                                    anyLabel={yearAnyLabel}
                                                    careerLabel={yearCareerLabel}
                                                    clearLabel={t('解除', 'Clear', 'Borrar')}
                                                    searchPlaceholder={yearSearchPlaceholder}
                                                    noResultLabel={yearNoResultLabel}
                                                />
                                            </div>
                                            <div className="year-filter-mobile">
                                                <div className="mobile-advanced-btn mb-2 flex-wrap items-center">
                                                    <span className="w-[100px] shrink-0 text-left font-semibold text-[var(--color-text-secondary)]">{t('年度', 'Season', 'Temporada')}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowYearModal('defense_season')}
                                                        disabled={playerSearching}
                                                        className="min-w-0 flex-1 cursor-pointer items-center justify-between gap-[6px] rounded-[4px] border border-[var(--color-border-default)] bg-[var(--color-surface-card)] p-2 text-[13px] text-[var(--color-text-secondary)]"
                                                    >
                                                        <span>{
                                                            selectedDefenseSeasonYear === null
                                                                ? yearAnyLabel
                                                                : selectedDefenseSeasonYear === 'career'
                                                                    ? yearCareerLabel
                                                                    : String(selectedDefenseSeasonYear)
                                                        }</span>
                                                        <span className="text-[10px] text-[#999]">▼</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="year-filter-row mb-2">
                                                <span className="year-filter-label">{t('集計', 'Scope', 'Criterio')}</span>
                                                <div className="year-combobox-wrap">
                                                    <select
                                                        value={selectedDefenseSearchMode}
                                                        onChange={(event) => {
                                                            const mode = event.target.value === 'position' ? 'position' : 'total';
                                                            handleDefenseSearchModeChange(mode);
                                                        }}
                                                        disabled={playerSearching}
                                                        className="pitcher-career-filter-op w-full"
                                                    >
                                                        <option value="total">{t('全守備合計', 'All Positions Total', 'Total de todas las posiciones')}</option>
                                                        <option value="position">{t('守備位置', 'Fielding Position', 'Posición defensiva')}</option>
                                                    </select>
                                                </div>
                                            </div>
                                            {selectedDefenseSearchMode === 'position' && (
                                                <div className="year-filter-row mb-2">
                                                    <span className="year-filter-label">{t('守備位置', 'Position', 'Posición')}</span>
                                                    <div className="year-combobox-wrap">
                                                        <select
                                                            value={selectedDefensePosition ?? ''}
                                                            onChange={(event) => {
                                                                const nextValue = event.target.value as DefensePositionKey | '';
                                                                setSelectedDefensePosition(nextValue === '' ? null : nextValue);
                                                            }}
                                                            disabled={playerSearching}
                                                            className="pitcher-career-filter-op w-full"
                                                        >
                                                            <option value="">{t('選択してください', 'Select position', 'Seleccione una posición')}</option>
                                                            {DEFENSE_POSITION_OPTIONS.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {t(option.ja, option.en, option.es)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="mb-2 text-[11px] text-[var(--color-text-muted)]">
                                                {selectedDefenseSearchMode === 'position'
                                                    ? t('選択した守備位置で判定', 'Filter by selected fielding position', 'Filtrar por la posición defensiva seleccionada')
                                                    : t('全守備位置の合計で判定', 'Total across all fielding positions', 'Total de todas las posiciones defensivas')}
                                            </div>
                                            <div className="pitcher-career-filter-grid">
                                                {DEFENSE_CAREER_FILTER_DEFS.map(stat => (
                                                    <div
                                                        key={stat.key}
                                                        className={`pitcher-career-filter-item${selectedDefenseUnifiedDraftFilters[stat.key].value.trim() ? ' is-filled' : ''}`}
                                                    >
                                                        <label className="pitcher-career-filter-label">{t(stat.ja, stat.en, stat.es)}</label>
                                                        <div className="pitcher-career-filter-controls">
                                                            <input
                                                                type="number"
                                                                inputMode="decimal"
                                                                step={stat.step}
                                                                value={selectedDefenseUnifiedDraftFilters[stat.key].value}
                                                                onChange={(event) => {
                                                                    if (isCareerYearSelected(selectedDefenseSeasonYear)) {
                                                                        handleDefenseCareerValueChange(stat.key, event.target.value);
                                                                    } else {
                                                                        handleDefenseSeasonValueChange(stat.key, event.target.value);
                                                                    }
                                                                }}
                                                                placeholder={getCareerValuePlaceholder(t, stat.key, stat.step)}
                                                                disabled={playerSearching || selectedDefenseSeasonYear === null}
                                                                className="pitcher-career-filter-value"
                                                            />
                                                            <select
                                                                value={selectedDefenseUnifiedDraftFilters[stat.key].op}
                                                                onChange={(event) => {
                                                                    const nextOp = event.target.value as PitcherCareerComparisonOperator;
                                                                    if (isCareerYearSelected(selectedDefenseSeasonYear)) {
                                                                        handleDefenseCareerOpChange(stat.key, nextOp);
                                                                    } else {
                                                                        handleDefenseSeasonOpChange(stat.key, nextOp);
                                                                    }
                                                                }}
                                                                disabled={playerSearching || selectedDefenseSeasonYear === null}
                                                                className="pitcher-career-filter-op"
                                                            >
                                                                <option value="gte">{t('以上', 'At least', 'Mínimo')}</option>
                                                                <option value="lte">{t('以下', 'At most', 'Máximo')}</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* 検索ボタン（PC/SP共通） */}
                                <div className="mt-2 flex justify-center border-t border-dashed border-[var(--color-border-subtle)] pt-2">
                                    <button
                                        onClick={() => {
                                            const nextAppliedPitcherCareerFilters = isCareerYearSelected(selectedPitcherSeasonYear)
                                                ? buildPitcherCareerAppliedFilters(selectedPitcherCareerFilters)
                                                : {};
                                            const nextAppliedBattingCareerFilters = isCareerYearSelected(selectedBattingSeasonYear)
                                                ? buildBattingCareerAppliedFilters(selectedBattingCareerFilters)
                                                : {};
                                            const nextAppliedDefenseCareerFilters = isCareerYearSelected(selectedDefenseSeasonYear)
                                                ? buildDefenseCareerAppliedFilters(selectedDefenseCareerFilters)
                                                : {};
                                            const nextAppliedPitcherSeasonFilters = isSeasonYearSelected(selectedPitcherSeasonYear)
                                                ? buildPitcherCareerAppliedFilters(selectedPitcherSeasonFilters)
                                                : {};
                                            const nextAppliedBattingSeasonFilters = isSeasonYearSelected(selectedBattingSeasonYear)
                                                ? buildBattingCareerAppliedFilters(selectedBattingSeasonFilters)
                                                : {};
                                            const nextAppliedDefenseSeasonFilters = isSeasonYearSelected(selectedDefenseSeasonYear)
                                                ? buildDefenseCareerAppliedFilters(selectedDefenseSeasonFilters)
                                                : {};
                                            setAppliedTeam(selectedTeam);
                                            setAppliedPitch(selectedPitch);
                                            setAppliedBat(selectedBat);
                                            setAppliedNationality(selectedNationality);
                                            setAppliedFirstYear(selectedFirstYear);
                                            setAppliedLastYear(selectedLastYear);
                                            setAppliedPitcherCareerFilters(nextAppliedPitcherCareerFilters);
                                            setAppliedBattingCareerFilters(nextAppliedBattingCareerFilters);
                                            setAppliedDefenseCareerFilters(nextAppliedDefenseCareerFilters);
                                            setAppliedDefenseSearchMode(selectedDefenseSearchMode);
                                            setAppliedDefensePosition(selectedDefenseSearchMode === 'position' ? selectedDefensePosition : null);
                                            setAppliedPitcherSeasonYear(isSeasonYearSelected(selectedPitcherSeasonYear) ? selectedPitcherSeasonYear : null);
                                            setAppliedPitcherSeasonFilters(nextAppliedPitcherSeasonFilters);
                                            setAppliedBattingSeasonYear(isSeasonYearSelected(selectedBattingSeasonYear) ? selectedBattingSeasonYear : null);
                                            setAppliedBattingSeasonFilters(nextAppliedBattingSeasonFilters);
                                            setAppliedDefenseSeasonYear(isSeasonYearSelected(selectedDefenseSeasonYear) ? selectedDefenseSeasonYear : null);
                                            setAppliedDefenseSeasonFilters(nextAppliedDefenseSeasonFilters);
                                            setPlayerPage(1);
                                            setSuppressInlineSearchStatus(true);
                                            setManualSearchTrigger(prev => prev + 1);
                                        }}
                                        disabled={playerSearching}
                                        className="cursor-pointer rounded-none border border-[var(--color-text-primary)] bg-[var(--color-surface-card)] px-5 py-[6px] text-[13px] font-normal text-[var(--color-text-primary)]"
                                    >
                                        {t('検索', 'Search', 'Buscar')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* スマホ用：プロ野球（NPB）チーム選択モーダル */}
                        {showNpbTeamModal && (
                            <div className="team-modal-overlay" onClick={() => setShowNpbTeamModal(false)}>
                                <div className="team-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="team-modal-header">
                                        <span className="team-modal-header-title">{t('プロ野球（NPB）チーム選択', 'Nippon Professional Baseball (NPB) Teams', 'Equipos de Béisbol Profesional Japonés (NPB)')}</span>
                                        <button className="team-modal-close-btn" onClick={() => setShowNpbTeamModal(false)}>✕</button>
                                    </div>
                                    <div className="team-modal-body">
                                        {/* NPB */}
                                        <div className="team-modal-league-section">
                                            <div className="team-modal-league-header">
                                                <span className="team-modal-league-name">{t('プロ野球（NPB）', 'Nippon Professional Baseball (NPB)', 'Béisbol Profesional Japonés (NPB)')}</span>
                                                <div className="team-modal-league-actions">
                                                    <button onClick={() => { const npbSlugs: string[] = [...CENTRAL_2025_ORDER, ...PACIFIC_2025_ORDER]; setSelectedTeam(prev => [...new Set([...prev, ...npbSlugs])]); }}>{t('全選択', 'All', 'Todos')}</button>
                                                    <button onClick={() => { const npbSlugs: string[] = [...CENTRAL_2025_ORDER, ...PACIFIC_2025_ORDER]; setSelectedTeam(prev => prev.filter(s => !npbSlugs.includes(s))); }}>{t('解除', 'Clear', 'Borrar')}</button>
                                                </div>
                                            </div>
                                            <div className="team-modal-division-label">{t('セ・リーグ', 'Central League', 'Liga Central')}</div>
                                            {CENTRAL_2025_ORDER.map(slug => (
                                                <label key={slug} className="team-modal-checkbox">
                                                    <input type="checkbox" checked={selectedTeam.includes(slug)} onChange={(e) => { if (e.target.checked) { setSelectedTeam(prev => [...prev, slug]); } else { setSelectedTeam(prev => prev.filter(s => s !== slug)); } }} />
                                                    {getTeamName(slug, slug)}
                                                </label>
                                            ))}
                                            <div className="team-modal-division-label">{t('パ・リーグ', 'Pacific League', 'Liga del Pacífico')}</div>
                                            {PACIFIC_2025_ORDER.map(slug => (
                                                <label key={slug} className="team-modal-checkbox">
                                                    <input type="checkbox" checked={selectedTeam.includes(slug)} onChange={(e) => { if (e.target.checked) { setSelectedTeam(prev => [...prev, slug]); } else { setSelectedTeam(prev => prev.filter(s => s !== slug)); } }} />
                                                    {getTeamName(slug, slug)}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="team-modal-footer">
                                        <button
                                            className="team-modal-search-btn"
                                            onClick={() => {
                                                setAppliedTeam(selectedTeam);
                                                setShowNpbTeamModal(false);
                                            }}
                                        >
                                            {t('設定', 'Set', 'Aceptar')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* スマホ用：メジャーリーグ（MLB）チーム選択モーダル */}
                        {showMlbTeamModal && (
                            <div className="team-modal-overlay" onClick={() => setShowMlbTeamModal(false)}>
                                <div className="team-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="team-modal-header">
                                        <span className="team-modal-header-title">{t('メジャーリーグ（MLB）チーム選択', 'Major League Baseball (MLB) Teams', 'Equipos de Grandes Ligas (MLB)')}</span>
                                        <button className="team-modal-close-btn" onClick={() => setShowMlbTeamModal(false)}>✕</button>
                                    </div>
                                    <div className="team-modal-body">
                                        {/* MLB */}
                                        <div className="team-modal-league-section">
                                            <div className="team-modal-league-header">
                                                <span className="team-modal-league-name">{t('メジャーリーグ（MLB）', 'Major League Baseball (MLB)', 'Grandes Ligas (MLB)')}</span>
                                                <div className="team-modal-league-actions">
                                                    <button onClick={() => { const mlbSlugs = mlbTeams.map(mt => mt.teamID); setSelectedTeam(prev => [...new Set([...prev, ...mlbSlugs])]); }}>{t('全選択', 'All', 'Todos')}</button>
                                                    <button onClick={() => { const mlbSlugs = mlbTeams.map(mt => mt.teamID); setSelectedTeam(prev => prev.filter(s => !mlbSlugs.includes(s))); }}>{t('解除', 'Clear', 'Borrar')}</button>
                                                </div>
                                            </div>
                                            {(['AL', 'NL'] as const).map(lg =>
                                                (['E', 'C', 'W'] as const).map(div => {
                                                    const divTeams = mlbTeams.filter(mt => mt.lgID === lg && mt.divID === div);
                                                    if (divTeams.length === 0) return null;
                                                    const lgLabel = lg === 'AL' ? t('アメリカン リーグ', 'AL', 'LA') : t('ナショナル リーグ', 'NL', 'LN');
                                                    const divLabel = div === 'E' ? t('東地区', 'East', 'Este') : div === 'C' ? t('中地区', 'Central', 'Central') : t('西地区', 'West', 'Oeste');
                                                    return (
                                                        <div key={`modal-${lg}-${div}`}>
                                                            <div className="team-modal-division-label">{lgLabel} {divLabel}</div>
                                                            {divTeams.map(mt => (
                                                                <label key={mt.teamID} className="team-modal-checkbox">
                                                                    <input type="checkbox" checked={selectedTeam.includes(mt.teamID)} onChange={(e) => { if (e.target.checked) { setSelectedTeam(prev => [...prev, mt.teamID]); } else { setSelectedTeam(prev => prev.filter(s => s !== mt.teamID)); } }} />
                                                                    {getTeamName(mt.teamID, mt.name)}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                    <div className="team-modal-footer">
                                        <button
                                            className="team-modal-search-btn"
                                            onClick={() => {
                                                setAppliedTeam(selectedTeam);
                                                setShowMlbTeamModal(false);
                                            }}
                                        >
                                            {t('設定', 'Set', 'Aceptar')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* スマホ用：国籍選択モーダル */}
                        {showNationalityModal && (
                            <div className="team-modal-overlay" onClick={() => setShowNationalityModal(false)}>
                                <div className="team-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="team-modal-header">
                                        <span className="team-modal-header-title">{t('国籍選択', 'Select Nationality', 'Seleccionar Nacionalidad')}</span>
                                        <button className="team-modal-close-btn" onClick={() => setShowNationalityModal(false)}>✕</button>
                                    </div>
                                    <div className="team-modal-body">
                                        <div className="team-modal-league-section">
                                            <div className="team-modal-league-header">
                                                <span className="team-modal-league-name">{t('国籍', 'Nationality', 'Nacionalidad')}</span>
                                                <div className="team-modal-league-actions">
                                                    <button onClick={() => setSelectedNationality(NATIONALITY_OPTIONS.map(o => o.value))}>{t('全選択', 'All', 'Todos')}</button>
                                                    <button onClick={() => setSelectedNationality([])}>{t('解除', 'Clear', 'Borrar')}</button>
                                                </div>
                                            </div>
                                            {NATIONALITY_OPTIONS.map(opt => (
                                                <label key={opt.value} className="team-modal-checkbox">
                                                    <input type="checkbox" checked={selectedNationality.includes(opt.value)} onChange={(e) => { if (e.target.checked) { setSelectedNationality(prev => [...prev, opt.value]); } else { setSelectedNationality(prev => prev.filter(s => s !== opt.value)); } }} />
                                                    {t(opt.ja, opt.en, opt.es)}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="team-modal-footer">
                                        <button
                                            className="team-modal-search-btn"
                                            onClick={() => {
                                                setAppliedNationality(selectedNationality);
                                                setShowNationalityModal(false);
                                            }}
                                        >
                                            {t('設定', 'Set', 'Aceptar')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* スマホ用：年選択モーダル */}
                        {showYearModal && (
                            <div className="team-modal-overlay" onClick={() => setShowYearModal(null)}>
                                <div className="team-modal year-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="team-modal-header">
                                        <span className="team-modal-header-title">{mobileYearLabel}</span>
                                        <button className="team-modal-close-btn" onClick={() => setShowYearModal(null)}>✕</button>
                                    </div>
                                    <div className="team-modal-body year-modal-body">
                                        <input
                                            type="text"
                                            value={mobileYearSearch}
                                            onChange={(event) => setMobileYearSearch(event.target.value)}
                                            placeholder={yearSearchPlaceholder}
                                            className="year-modal-search-input"
                                            inputMode="numeric"
                                            autoFocus
                                        />
                                        <div className="year-modal-list">
                                            <button
                                                type="button"
                                                className={`year-modal-option${mobileYearValue === null ? ' is-selected' : ''}`}
                                                onClick={() => handleMobileYearSelect(null)}
                                            >
                                                {yearAnyLabel}
                                            </button>
                                            {mobileYearSupportsCareer && (
                                                <button
                                                    type="button"
                                                    className={`year-modal-option${mobileYearValue === 'career' ? ' is-selected' : ''}`}
                                                    onClick={() => handleMobileYearSelect('career')}
                                                >
                                                    {yearCareerLabel}
                                                </button>
                                            )}
                                            {mobileYearOptions.length === 0 ? (
                                                <div className="year-modal-empty">{yearNoResultLabel}</div>
                                            ) : (
                                                mobileYearOptions.map(year => (
                                                    <button
                                                        key={`modal-${year}`}
                                                        type="button"
                                                        className={`year-modal-option${mobileYearValue === year ? ' is-selected' : ''}`}
                                                        onClick={() => handleMobileYearSelect(year)}
                                                    >
                                                        {year}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {(playerQuery.trim() !== '' || manualSearchTrigger > 0) && playerResults.length === 0 && !playerSearching && (
                        <p className="empty-state">{t('該当する選手は見つかりませんでした。', 'No players found.', 'No se encontraron jugadores.')}</p>
                    )}

                    {(playerResults.length > 0 || playerSearching) && (
                        <>
                            {playerResults.length > 0 && (
                                <>
                                    <div className="player-count">
                                        {t(
                                            `${playerTotal}選手見つかりました（${pageStartIndex}-${pageEndIndex}件表示）`,
                                            `${playerTotal} players found (showing ${pageStartIndex}-${pageEndIndex})`,
                                            `${playerTotal} jugadores encontrados (mostrando ${pageStartIndex}-${pageEndIndex})`
                                        )}
                                    </div>
                                    {renderPagination('top')}
                                </>
                            )}
                            <div className="player-table-container">
                                {playerResults.length > 0 ? (
                                    <>
                                        {playerSearching && (
                                            <div className="player-table-loading-overlay">
                                                <div className="player-table-spinner" />
                                                <span className="player-table-loading-text">{t('検索中...', 'Searching...', 'Buscando...')}</span>
                                            </div>
                                        )}
                                        <table className="player-table">
                                            <thead>
                                                <tr>
                                                    <th className="col-name">{t('選手名', 'Name', 'Nombre')}</th>
                                                    <th className="col-desktop col-position">{t('ポジション', 'Position', 'Posición')}</th>
                                                    <th className="col-desktop col-team">{t('球団', 'Team', 'Equipo')}</th>
                                                    <th className="col-desktop col-nationality">{t('国籍', 'Nationality', 'Nacionalidad')}</th>
                                                    <th className="col-desktop col-birthday">{t('生年月日', 'Birthday', 'Fecha de nacimiento')}</th>
                                                    <th className="col-mobile col-info text-[0.9em] font-medium text-[var(--color-text-muted)]">
                                                        {t('ポジション / 球団 / 国籍 / 生年月日', 'Position / Team / Nationality / Birthday', 'Pos. / Equ. / Nac. / Fecha de n.')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {playerResults.map((p) => (
                                                    <tr
                                                        key={p.profileId}
                                                        className="clickable-row"
                                                        onClick={(e) => handleRowClick(e, toLocalePath(`/players/${p.slug}${p.teamSlug ? `?team=${p.teamSlug}&from=players` : '?from=players'}`))}
                                                    >
                                                        <td className="col-name">
                                                            <Link href={toLocalePath(`/players/${p.slug}${p.teamSlug ? `?team=${p.teamSlug}&from=players` : '?from=players'}`)} className="player-link">
                                                                {val(p.name, p.nameEn)}
                                                            </Link>
                                                        </td>
                                                        {/* PC用カラム */}
                                                        <td className="col-desktop col-position">{valStrict(translatePosition(p.mainPosition || p.position, locale), translatePosition(p.mainPositionEn || p.positionEn, locale))}</td>
                                                        <td className="col-desktop col-team">
                                                            {getTeamName(p.teamSlug || '', p.teamName || '-')}
                                                        </td>
                                                        <td className="col-desktop col-nationality">{locale === 'es' ? (p.nationalityEs || p.nationalityEn || '-') : valStrict(translateNationality(p.nationality, locale), translateNationality(p.nationalityEn, locale))}</td>
                                                        <td className="col-desktop col-birthday">{p.birthdayDate ? formatBirthday(p.birthdayDate, !!p.deathDate, locale as any) : (p.birthday || '')}</td>
                                                        {/* スマホ用カラム */}
                                                        <td className="col-mobile col-info">
                                                            <div className="player-info-cell">
                                                                <span className="player-info-value">{valStrict(translatePosition(p.mainPosition || p.position, locale), translatePosition(p.mainPositionEn || p.positionEn, locale))}</span>
                                                                <span className="player-info-value">{getTeamName(p.teamSlug || '', p.teamName || '-')}</span>
                                                                <span className="player-info-value">{locale === 'es' ? (p.nationalityEs || p.nationalityEn || '-') : valStrict(translateNationality(p.nationality, locale), translateNationality(p.nationalityEn, locale))}</span>
                                                                <span className="player-info-value">{p.birthdayDate ? formatBirthday(p.birthdayDate, !!p.deathDate, locale as any) : (p.birthday || '')}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </>
                                ) : (
                                    <div className="player-table-loading-only">
                                        <div className="player-table-spinner" />
                                        <span className="player-table-loading-text">{t('検索中...', 'Searching...')}</span>
                                    </div>
                                )}
                            </div>
                            {playerResults.length > 0 && renderPagination('bottom')}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// useSearchParams をラップして spv を渡す
function HomeWithSearchParams(props: Omit<HomeProps, 'spv'>) {
    const searchParams = useSearchParams();
    const getParam = (key: string): string | null => searchParams?.get(key) ?? null;
    const spv: HomeSearchParamValues = {
        tab: getParam('tab'),
        q: getParam('q'),
        pos: getParam('pos'),
        active: getParam('active'),
        ob: getParam('ob'),
        team: getParam('team'),
        page: getParam('page'),
    };
    return <HomeInner {...props} spv={spv} />;
}

export const NpbTeamListClient = ({ locale, initialNpbTeams }: { locale: Locale; initialNpbTeams?: Team[] }) => (
    <Suspense fallback={
        <HomeInner locale={locale} forcedTab="npb" initialNpbTeams={initialNpbTeams} spv={DEFAULT_HOME_SPV} />
    }>
        <HomeWithSearchParams locale={locale} forcedTab="npb" initialNpbTeams={initialNpbTeams} />
    </Suspense>
);

export const MlbTeamListClient = ({ locale, initialMlbTeams }: { locale: Locale; initialMlbTeams?: MlbTeam[] }) => (
    <Suspense fallback={
        <HomeInner locale={locale} forcedTab="mlb" initialMlbTeams={initialMlbTeams} spv={DEFAULT_HOME_SPV} />
    }>
        <HomeWithSearchParams locale={locale} forcedTab="mlb" initialMlbTeams={initialMlbTeams} />
    </Suspense>
);

export const PlayerSearchClient = ({
    locale,
    initialNpbTeams,
    initialMlbTeams,
}: {
    locale: Locale;
    initialNpbTeams?: Team[];
    initialMlbTeams?: MlbTeam[];
}) => (
    <Suspense fallback={
        <HomeInner
            locale={locale}
            forcedTab="players"
            initialNpbTeams={initialNpbTeams}
            initialMlbTeams={initialMlbTeams}
            spv={DEFAULT_HOME_SPV}
        />
    }>
        <HomeWithSearchParams
            locale={locale}
            forcedTab="players"
            initialNpbTeams={initialNpbTeams}
            initialMlbTeams={initialMlbTeams}
        />
    </Suspense>
);
